/**
 * render/character_create.ts — Character Creation Screen
 *
 * Where you become yourself. A warm, unhurried space for choosing your look
 * before stepping into the world. Every click is a small act of self-expression.
 *
 * Fully responsive: adapts to portrait/landscape, any DPR, touch or mouse.
 * Integrates with wardrobe catalog for named pieces and starter inventory.
 *
 * Layout modes:
 *   LANDSCAPE (W >= H*1.1): preview left, controls right, name+button bottom
 *   PORTRAIT  (W <  H*1.1): preview top, controls middle, name+button bottom
 */

import { GameState, type InventoryItem } from "../engine/state.js";
import type { InputState, PointerPosition } from "../engine/input.js";
import {
  type CharacterAppearance as ComposerAppearance,
  type BodyArchetypeId,
  type BodyGender,
  type CharacterState,
  type Direction,
  type Expression,
  createCharacter,
  renderCharacter,
  invalidateCharacter,
  updateAnimation,
  defaultAppearance,
  setDeviceContext,
  setDeviceCaseContext,
  setAttentionContext,
  SKIN_TONES,
  HAIR_COLORS,
  EYE_COLORS,
  ITEM_COLORS,
  MAKEUP_COLORS,
  LIP_COLORS,
} from "../character/composer.js";
import {
  type AttentionState,
  createAttentionState,
  updateAttention,
} from "../character/attention.js";
import { setComposerAppearance } from "./bridge.js";
import { getPiecesForSlot } from "../character/wardrobe.js";

// ─── Palette ─────────────────────────────────────────────────────────────────

const C = {
  bg:          "#0d0a08",
  bgLight:     "#1a1410",
  panel:       "#1a150fcc",
  panelBorder: "#2a2018",
  text:        "#e8d0b0",
  textDim:     "#8a7a6a",
  accent:      "#c4956a",
  accentDim:   "#8a6a4a",
  accentHover: "#d4a57a",
  warm:        "#d4854a",
  highlight:   "#f0d8a0",
  catActive:   "#2a2018",
  catInactive: "#12100a",
  nameActiveBg:"#1e180f",
  nameBg:      "#1a1410",
  nameBorder:  "#3a2a1a",
  nameText:    "#f0d8a0",
  swatch:      "#0d0a08",
  swatchBorder:"#3a2a1a",
  arrow:       "#c4956a",
  arrowDim:    "#5a4a3a",
  btnDark:     "#1a0a04",
} as const;

// ─── Category / Attribute system ─────────────────────────────────────────────

type CategoryId = "body" | "hair" | "face" | "outfit" | "extras" | "devices";

interface AttributeDef {
  key: keyof ComposerAppearance;
  label: string;
  min: number;
  max: number;
  palette?: readonly string[];
  valueLabels?: readonly string[];
  /** Wardrobe slot to pull named labels from */
  wardrobeSlot?: string;
  category: CategoryId;
}

const BODY_LABELS      = ["Slim", "Average", "Broad", "Petite"] as const;
const ARCHETYPE_LABELS = ["Athletic", "Soft", "Lithe", "Powerful", "Petite"] as const;
const ARCHETYPE_IDS: BodyArchetypeId[] = ["athletic", "soft", "lithe", "powerful", "petite"];
const BLEND_LABELS     = ["Primary", "Mostly 1st", "Even", "Mostly 2nd", "Secondary"] as const;
const SCALE_LABELS     = ["Slight", "Lean", "Medium", "Full", "Grand"] as const;
const GENDER_LABELS    = ["Feminine", "Masculine", "Androgynous"] as const;
const GENDER_IDS: BodyGender[] = ["feminine", "masculine", "androgynous"];
const NUDE_LABELS      = ["Clothed", "Nude"] as const;
const BREAST_LABELS    = ["Flat", "Small", "Medium", "Large", "Very Large"] as const;
const SHOULDER_ADJ_LABELS = ["Narrow", "Lean", "Natural", "Broad", "Wide"] as const;
const WAIST_ADJ_LABELS    = ["Cinched", "Slim", "Natural", "Thick", "Wide"] as const;
const HIP_ADJ_LABELS      = ["Narrow", "Slim", "Natural", "Full", "Wide"] as const;
const LEG_GAP_LABELS      = ["Touching", "Close", "Natural", "Open", "Wide"] as const;
const BUTT_LABELS      = ["Flat", "Toned", "Round", "Full", "Voluptuous"] as const;
const GENITAL_LABELS   = ["Small", "Below Avg", "Average", "Above Avg", "Large"] as const;
const FACE_LABELS  = ["Friendly", "Serene", "Curious", "Wry", "Gentle", "Determined", "Soft Smile", "Freckled"] as const;

const HAIR_NAMES = [
  "Crew Cut","Side Part","Afro","Long Straight","Messy Bedhead",
  "Wavy Long","Undercut","Ponytail","Bob","Long Curly",
  "Spiky Punk","Side Sweep","Mohawk","Braids","Top Knot","Layered",
  "Pixie Cut","Curtain Bangs","Wolf Cut","Space Buns",
  "Half-Up","Cornrows","Shag","High Ponytail"
] as const;

const TOP_NAMES = [
  "Basic Tee", "Breton Stripe", "Cozy Hoodie", "Flowy Tunic", "Button-Up",
] as const;
const BOTTOM_NAMES = [
  "Straight Pants", "Casual Shorts", "A-Line Skirt", "Cargo Pants",
] as const;
const SHOE_NAMES = [
  "Canvas Sneakers", "Lace-Up Boots", "Heels", "Strappy Sandals",
] as const;
const HAT_NAMES = [
  "None", "Knit Beanie", "Baseball Cap", "Top Hat",
  "Crown", "Witch Hat", "Beret", "Halo", "Bucket Hat",
] as const;
const JACKET_NAMES = [
  "None", "Open Cardigan", "Bomber Jacket", "Trench Coat",
] as const;
const ACCESSORY_NAMES = [
  "None", "Wool Scarf", "Round Glasses", "Small Earrings",
  "Simple Necklace", "Bandana",
] as const;
const AURA_NAMES = [
  "None", "Ember Glow", "Bloom Petals", "Frost Shimmer", "Pulse Rings",
] as const;
const SKIN_DETAIL_NAMES = [
  "None", "Freckles Light", "Freckles Dark", "Beauty Mark",
] as const;
const FACIAL_HAIR_NAMES = [
  "None", "Stubble", "Goatee", "Full Beard", "Mustache", "Soul Patch", "Mutton Chops",
] as const;
const MAKEUP_NAMES = [
  "None", "Natural", "Glam", "Smoky Eye", "Cat Eye", "Blush Glow", "Avant-Garde",
] as const;
const LIP_NAMES = [
  "None", "Subtle Tint", "Matte", "Gloss", "Bold", "Ombré",
] as const;
const WINGS_NAMES = [
  "None", "Angel", "Butterfly", "Bat", "Fairy",
] as const;
const CAPE_NAMES = [
  "None", "Short Cape", "Long Cape", "Hooded Cloak",
] as const;
const BACKPACK_NAMES = [
  "None", "Small Pack", "Hiking Pack", "Satchel",
] as const;

/** Expression values — maps index to Expression string for the face attribute */
const EXPRESSION_VALUES: readonly Expression[] = [
  "neutral", "smile", "frown", "surprise", "laugh", "sleepy",
] as const;
const EXPRESSION_NAMES = [
  "Neutral", "Smile", "Frown", "Surprise", "Laugh", "Sleepy",
] as const;

/** Tooltip descriptions for each attribute key */
const ATTR_TOOLTIPS: Record<string, string> = {
  bodyType:         "Your character's body shape and build",
  skinTone:         "Choose from a range of natural skin tones",
  skinDetail:       "Subtle features like freckles or beauty marks",
  hairStyle:        "Each style looks different from every angle",
  hairColor:        "From natural shades to bold self-expression",
  eyeStyle:         "How your character looks at the world",
  eyeColor:         "The color that catches the firelight",
  facialHairStyle:  "Stubble, beards, mustaches — add character",
  facialHairColor:  "Match your hair or go your own way",
  makeupStyle:      "From subtle to statement — your face, your canvas",
  makeupColor:      "Eyeshadow and accent tones",
  lipStyle:         "From barely-there to bold — every shade tells a story",
  lipColor:         "Find your shade",
  topStyle:         "From basic tees to flowing tunics — your vibe, your rules",
  topColor:         "Pick a color that feels like you",
  bottomStyle:      "Pants, shorts, skirts — dress for the life you want",
  bottomColor:      "Coordinate or contrast — your call",
  shoeStyle:        "Sneakers, boots, heels, or sandals — every step counts",
  shoeColor:        "Grounded from the ground up",
  hatStyle:         "Beanies to halos — crown yourself however you like",
  hatColor:         "Accent your look from the top",
  jacketStyle:      "Cardigans, bombers, trench coats — layer up",
  jacketColor:      "Layer your colors thoughtfully",
  heldItem:         "Something to hold — a small comfort",
  accessoryStyle:   "Scarves, glasses, jewelry — the details that define you",
  accessoryColor:   "A finishing accent color",
  auraStyle:        "A subtle glow that follows you — purely cosmetic",
  expression:       "How your character greets the world — your resting face",
  wingsStyle:       "Angel, butterfly, bat, or fairy — spread your wings",
  wingsColor:       "Color your wings to match your soul",
  capeStyle:        "Short, long, or hooded — every hero needs a cape",
  capeColor:        "A flowing color behind you",
  backpackStyle:    "Carry your world with you — small, hiking, or satchel",
  backpackColor:    "Adventure comes in every color",
  bodyArchetype1:   "Your primary body shape — athletic, soft, lithe, powerful, or petite",
  bodyArchetype2:   "A secondary build to blend toward for unique proportions",
  archetypeBlend:   "How much of your secondary build shows through",
  bodyScale:        "Overall body size — from slight to grand",
  shoulderAdjust:   "Fine-tune shoulder width independently of build",
  waistAdjust:      "Adjust waist definition — cinched for drama, wide for strength",
  hipAdjust:        "Shape your hip line — from narrow to full",
  legGap:           "How close your thighs sit — affected by anatomy and build",
  bodyGender:       "Anatomical presentation — affects body details when unclothed",
  breastSize:       "From flat to full — your body, your choice",
  buttSize:         "Shape what you're working with — flat to voluptuous",
  genitalSize:      "Everybody's different — own it",
  nude:             "Toggle clothing on or off — see your body as it is",
};

const CATEGORIES: { id: CategoryId; label: string }[] = [
  { id: "body",    label: "Body" },
  { id: "hair",    label: "Hair" },
  { id: "face",    label: "Face" },
  { id: "outfit",  label: "Outfit" },
  { id: "extras",  label: "Extras" },
  { id: "devices", label: "Devices" },
];

// ─── Device color palette ─────────────────────────────────────────────────
// Curated phone/pad shell colors — real-world inspired

const DEVICE_COLORS: { hex: string; name: string; group: string }[] = [
  // Classics
  { hex: "#3a3a3a", name: "Space Gray",    group: "Classic" },
  { hex: "#c0c0c0", name: "Silver",        group: "Classic" },
  { hex: "#f5f0e8", name: "Starlight",     group: "Classic" },
  { hex: "#1c1c2e", name: "Midnight",      group: "Classic" },
  { hex: "#2a2a2a", name: "Obsidian",      group: "Classic" },
  { hex: "#e0e0e0", name: "Pearl",         group: "Classic" },
  // Earth
  { hex: "#5b3a29", name: "Espresso",      group: "Earth" },
  { hex: "#c49056", name: "Amber",         group: "Earth" },
  { hex: "#e8c8a0", name: "Sand",          group: "Earth" },
  { hex: "#8a6e4a", name: "Walnut",        group: "Earth" },
  { hex: "#d4a07a", name: "Terracotta",    group: "Earth" },
  { hex: "#f0dcc0", name: "Cream",         group: "Earth" },
  // Nature
  { hex: "#2a4a3a", name: "Forest",        group: "Nature" },
  { hex: "#c0d0a0", name: "Sage",          group: "Nature" },
  { hex: "#4a6a50", name: "Moss",          group: "Nature" },
  { hex: "#90b090", name: "Fern",          group: "Nature" },
  { hex: "#1a3a2a", name: "Pine",          group: "Nature" },
  { hex: "#d0e0c0", name: "Mint",          group: "Nature" },
  // Sky & Water
  { hex: "#1a3a5a", name: "Ocean",         group: "Sky" },
  { hex: "#a0c8e0", name: "Sky",           group: "Sky" },
  { hex: "#2a4a6a", name: "Denim",         group: "Sky" },
  { hex: "#6090b0", name: "Steel Blue",    group: "Sky" },
  { hex: "#c0d8e8", name: "Ice",           group: "Sky" },
  { hex: "#1a2a4a", name: "Navy",          group: "Sky" },
  // Warm
  { hex: "#8b1a1a", name: "Crimson",       group: "Warm" },
  { hex: "#f0a0b0", name: "Rose",          group: "Warm" },
  { hex: "#c04040", name: "Cherry",        group: "Warm" },
  { hex: "#e07050", name: "Coral",         group: "Warm" },
  { hex: "#f0c0c0", name: "Blush",         group: "Warm" },
  { hex: "#d06030", name: "Ember",         group: "Warm" },
  // Bold
  { hex: "#4a2a5a", name: "Violet",        group: "Bold" },
  { hex: "#6a3a7a", name: "Amethyst",      group: "Bold" },
  { hex: "#c080d0", name: "Lavender",      group: "Bold" },
  { hex: "#e0b020", name: "Gold",          group: "Bold" },
  { hex: "#f0d060", name: "Sunflower",     group: "Bold" },
  { hex: "#20c0a0", name: "Teal",          group: "Bold" },
];

type DeviceFinish = "matte" | "glossy" | "metallic";
const FINISH_LABELS: Record<DeviceFinish, string> = {
  matte: "Matte", glossy: "Glossy", metallic: "Metallic",
};

// ─── Real-world proportional device models ──────────────────────────────────
// All dimensions in mm (real-world), rendered with a consistent mm→px scale.
// Phone aspect ≈ 1:2.05, Pad aspect ≈ 1:1.38 (landscape).

interface DeviceModel {
  id: string;
  name: string;
  widthMM: number;   // physical width in mm
  heightMM: number;  // physical height in mm (phone: portrait, pad: landscape height)
  depthMM: number;   // thickness in mm
  screenInset: number; // bezel fraction (0–1) per side
  cornerPct: number;  // corner radius as fraction of width
  label: string;      // short display label
}

const PHONE_MODELS: DeviceModel[] = [
  { id: "standard", name: "hPhone",      widthMM: 72, heightMM: 148, depthMM: 7.8, screenInset: 0.05, cornerPct: 0.16, label: "6.1\"" },
  { id: "plus",     name: "hPhone Plus",  widthMM: 78, heightMM: 161, depthMM: 7.8, screenInset: 0.05, cornerPct: 0.15, label: "6.7\"" },
  { id: "mini",     name: "hPhone Mini",  widthMM: 64, heightMM: 131, depthMM: 7.4, screenInset: 0.06, cornerPct: 0.17, label: "5.4\"" },
];

const PAD_MODELS: DeviceModel[] = [
  { id: "11inch", name: "hPad 11\"", widthMM: 249, heightMM: 178, depthMM: 5.9, screenInset: 0.03, cornerPct: 0.11, label: "11\"" },
  { id: "13inch", name: "hPad 13\"", widthMM: 281, heightMM: 215, depthMM: 6.1, screenInset: 0.03, cornerPct: 0.10, label: "13\"" },
];

// mm → pixel conversion at base scale 1.0. Chosen so the standard phone ≈ 42px wide,
// giving the 11" pad ≈ 146px wide (landscape) — a realistic ~3.5× ratio.
const MM_TO_PX = 0.585;

function modelPx(model: DeviceModel, s: number): { w: number; h: number; d: number; cr: number; inset: number } {
  return {
    w: Math.round(model.widthMM * MM_TO_PX * s),
    h: Math.round(model.heightMM * MM_TO_PX * s),
    d: Math.round(model.depthMM * MM_TO_PX * s),
    cr: Math.round(model.widthMM * model.cornerPct * MM_TO_PX * s),
    inset: Math.round(model.widthMM * model.screenInset * MM_TO_PX * s),
  };
}

/** Compute scale factor so a device model fits within maxW × maxH, preserving aspect ratio */
function deviceFitScale(model: DeviceModel, maxW: number, maxH: number): number {
  const baseW = model.widthMM * MM_TO_PX;
  const baseH = model.heightMM * MM_TO_PX;
  return Math.min(maxW / baseW, maxH / baseH);
}

const ATTRIBUTES: AttributeDef[] = [
  // Body
  { key: "bodyType",    label: "Build",        min: 0,  max: 3,  valueLabels: BODY_LABELS,  category: "body" },
  { key: "skinTone",    label: "Skin Tone",    min: 0,  max: 11, palette: SKIN_TONES,       category: "body" },
  { key: "skinDetail",  label: "Skin Detail",  min: -1, max: 2,  valueLabels: SKIN_DETAIL_NAMES, category: "body" },
  { key: "bodyArchetype1", label: "Primary Build",   min: 0, max: 4, valueLabels: ARCHETYPE_LABELS, category: "body" },
  { key: "bodyArchetype2", label: "Secondary Build", min: 0, max: 4, valueLabels: ARCHETYPE_LABELS, category: "body" },
  { key: "archetypeBlend", label: "Blend",           min: 0, max: 4, valueLabels: BLEND_LABELS,     category: "body" },
  { key: "bodyScale",      label: "Body Scale",      min: 0, max: 4, valueLabels: SCALE_LABELS,     category: "body" },
  { key: "shoulderAdjust", label: "Shoulders",       min: 0, max: 4, valueLabels: SHOULDER_ADJ_LABELS, category: "body" },
  { key: "waistAdjust",    label: "Waist",           min: 0, max: 4, valueLabels: WAIST_ADJ_LABELS,    category: "body" },
  { key: "hipAdjust",      label: "Hips",            min: 0, max: 4, valueLabels: HIP_ADJ_LABELS,      category: "body" },
  { key: "legGap",         label: "Leg Gap",         min: 0, max: 4, valueLabels: LEG_GAP_LABELS,      category: "body" },
  { key: "bodyGender",     label: "Anatomy",         min: 0, max: 2, valueLabels: GENDER_LABELS,    category: "body" },
  { key: "breastSize",    label: "Breast Size",     min: 0, max: 4, valueLabels: BREAST_LABELS,    category: "body" },
  { key: "buttSize",      label: "Butt Size",       min: 0, max: 4, valueLabels: BUTT_LABELS,      category: "body" },
  { key: "genitalSize",   label: "Genital Size",    min: 0, max: 4, valueLabels: GENITAL_LABELS,   category: "body" },
  { key: "nude",           label: "Clothing",        min: 0, max: 1, valueLabels: NUDE_LABELS,      category: "body" },
  // Hair
  { key: "hairStyle",   label: "Style",        min: 0,  max: 23, valueLabels: HAIR_NAMES,   category: "hair" },
  { key: "hairColor",   label: "Color",        min: 0,  max: 19, palette: HAIR_COLORS,      category: "hair" },
  // Face
  { key: "eyeStyle",       label: "Eye Shape",     min: 0,  max: 7,  valueLabels: FACE_LABELS,       category: "face" },
  { key: "eyeColor",       label: "Eye Color",     min: 0,  max: 11, palette: EYE_COLORS,            category: "face" },
  { key: "facialHairStyle", label: "Facial Hair",  min: -1, max: 5,  valueLabels: FACIAL_HAIR_NAMES, category: "face" },
  { key: "facialHairColor", label: "Beard Color",  min: 0,  max: 19, palette: HAIR_COLORS,           category: "face" },
  { key: "makeupStyle",    label: "Makeup",        min: -1, max: 5,  valueLabels: MAKEUP_NAMES,      category: "face" },
  { key: "makeupColor",    label: "Makeup Color",  min: 0,  max: 19, palette: MAKEUP_COLORS,         category: "face" },
  { key: "lipStyle",       label: "Lips",          min: -1, max: 4,  valueLabels: LIP_NAMES,         category: "face" },
  { key: "lipColor",       label: "Lip Color",     min: 0,  max: 19, palette: LIP_COLORS,            category: "face" },
  { key: "expression",     label: "Expression",    min: 0,  max: 5,  valueLabels: EXPRESSION_NAMES,  category: "face" },
  // Outfit — max values match composer variant counts (style % N). min: -1 = none (undressed)
  { key: "topStyle",    label: "Top",          min: -1, max: 4,  valueLabels: ["None", ...TOP_NAMES],    category: "outfit" },
  { key: "topColor",    label: "Top Color",    min: 0,  max: 35, palette: ITEM_COLORS,      category: "outfit" },
  { key: "bottomStyle", label: "Bottom",       min: -1, max: 3,  valueLabels: ["None", ...BOTTOM_NAMES], category: "outfit" },
  { key: "bottomColor", label: "Bottom Color", min: 0,  max: 35, palette: ITEM_COLORS,      category: "outfit" },
  { key: "shoeStyle",   label: "Shoes",        min: -1, max: 3,  valueLabels: ["None", ...SHOE_NAMES],   category: "outfit" },
  { key: "shoeColor",   label: "Shoe Color",   min: 0,  max: 35, palette: ITEM_COLORS,      category: "outfit" },
  // Extras
  { key: "hatStyle",    label: "Hat",          min: -1, max: 7,  valueLabels: HAT_NAMES,    category: "extras" },
  { key: "hatColor",    label: "Hat Color",    min: 0,  max: 35, palette: ITEM_COLORS,      category: "extras" },
  { key: "jacketStyle", label: "Jacket",       min: -1, max: 2,  valueLabels: JACKET_NAMES, category: "extras" },
  { key: "jacketColor", label: "Jacket Color", min: 0,  max: 35, palette: ITEM_COLORS,      category: "extras" },
  { key: "heldItem",       label: "Held Item",    min: -1, max: 2,  valueLabels: ["None","Cup of Tea","Open Book","Sketchbook"] as unknown as readonly string[], category: "extras" },
  { key: "accessoryStyle", label: "Accessory",    min: -1, max: 4,  valueLabels: ACCESSORY_NAMES, category: "extras" },
  { key: "accessoryColor", label: "Acc. Color",   min: 0,  max: 35, palette: ITEM_COLORS,         category: "extras" },
  { key: "auraStyle",      label: "Aura",         min: -1, max: 3,  valueLabels: AURA_NAMES,      category: "extras" },
  { key: "wingsStyle",     label: "Wings",        min: -1, max: 3,  valueLabels: WINGS_NAMES,     category: "extras" },
  { key: "wingsColor",     label: "Wings Color",  min: 0,  max: 35, palette: ITEM_COLORS,         category: "extras" },
  { key: "capeStyle",      label: "Cape",         min: -1, max: 2,  valueLabels: CAPE_NAMES,      category: "extras" },
  { key: "capeColor",      label: "Cape Color",   min: 0,  max: 35, palette: ITEM_COLORS,         category: "extras" },
  { key: "backpackStyle",  label: "Backpack",     min: -1, max: 2,  valueLabels: BACKPACK_NAMES,  category: "extras" },
  { key: "backpackColor",  label: "Back. Color",  min: 0,  max: 35, palette: ITEM_COLORS,         category: "extras" },
];

// ─── Hit Region ──────────────────────────────────────────────────────────────

interface HitRegion {
  x: number; y: number; w: number; h: number;
  action: () => void;
}

// ─── Direction rotation ──────────────────────────────────────────────────────

const DIRS: Direction[] = ["s", "se", "e", "ne", "n", "nw", "w", "sw"];
const AUTO_ROTATE_SEC = 2.5;

// ─── Responsive helpers ──────────────────────────────────────────────────────

function isLandscape(W: number, H: number): boolean { return W >= H * 1.1; }
/** Scale-factor for UI elements based on smallest viewport dimension */
function uiScale(W: number, H: number): number {
  const base = Math.min(W, H);
  if (base < 400) return 0.75;
  if (base < 600) return 0.875;
  if (base > 1200) return 1.15;
  return 1.0;
}
/** Touch-friendly minimum hit target — never below 44px (WCAG minimum) */
function minTap(s: number): number { return Math.max(44 * s, 44); }

// ─── Renderer ────────────────────────────────────────────────────────────────

export class CharacterCreateRenderer {
  private ctx: CanvasRenderingContext2D;
  private appearance: ComposerAppearance;
  private charState: CharacterState | null = null;

  // Name
  private playerName = "";
  private nameEditing = false;
  private cursorVis = true;
  private cursorTimer = 0;

  // Preview
  private dirIndex = 0;
  private dirTimer = 0;
  private manualDir = false; // user clicked direction arrows

  // Category tabs
  private activeCategory: CategoryId = "body";

  // Scroll
  private scrollY = 0;
  private maxScrollY = 0;

  // Hit regions (rebuilt each frame)
  private hits: HitRegion[] = [];

  // Tooltip (set during draw, rendered last)
  private tooltipText = "";
  private tooltipX = 0;
  private tooltipY = 0;

  // Completion
  private _ready = false;

  // Starter items chosen via accessories
  private starterItems: InventoryItem[] = [];

  // Device customization
  private phoneColor = "#3a3a3a";  // default space gray
  private padColor   = "#c0c0c0";  // default silver
  private phoneFinish: DeviceFinish = "matte";
  private padFinish: DeviceFinish = "matte";
  private deviceFocus: "phone" | "pad" = "phone";
  private phoneModelIdx = 0;  // index into PHONE_MODELS
  private padModelIdx = 0;    // index into PAD_MODELS
  private devicePreviewAngle = 0;   // smooth rotation angle for 3D tilt
  private deviceAutoRotate = true;  // auto-rotate to show from all angles
  private deviceColorGroup = "Classic"; // current color filter group
  private deviceCharState: CharacterState | null = null; // separate char for device preview

  // Hover tracking
  private hoverX = 0;
  private hoverY = 0;

  // Attention engine — makes the preview character feel ALIVE
  private attentionState: AttentionState = createAttentionState();
  /** Synthetic idle hair sway phase — gentle breathing motion even when still */
  private idleHairPhase = 0;

  // Animation state for "alive" feeling
  private lastChangedAttr = "";  // key of last changed attribute
  private lastChangeTime = 0;    // timestamp of last change
  private tabSwitchTime = 0;     // timestamp of last tab switch
  private beginBtnHover = false;
  private globalTime = 0;        // accumulated time for breathing effects

  // Audio context for UI sounds (lightweight, separate from game AudioEngine)
  private audioCtx: AudioContext | null = null;
  private audioGain: GainNode | null = null;
  private clickCounter = 0; // for pitch variation

  constructor(_canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) {
    this.ctx = ctx;
    this.appearance = defaultAppearance();
  }

  // ─── Public API ──────────────────────────────────────────────────────────

  get ready(): boolean { return this._ready; }

  getPlayerName(): string {
    return this.playerName.trim() || "Newcomer";
  }

  /** Starter inventory items based on accessory/hat/held choices */
  getStarterItems(): InventoryItem[] {
    return [...this.starterItems];
  }

  /** Device shell colors and models chosen during creation */
  getDeviceColors(): { phoneColor: string; padColor: string; phoneModelId: string; padModelId: string } {
    return {
      phoneColor: this.phoneColor,
      padColor: this.padColor,
      phoneModelId: this.phoneModel.id,
      padModelId: this.padModel.id,
    };
  }

  /** Currently selected device models */
  get phoneModel(): DeviceModel { return PHONE_MODELS[this.phoneModelIdx]; }
  get padModel(): DeviceModel { return PAD_MODELS[this.padModelIdx]; }

  // ─── Update ─────────────────────────────────────────────────────────────

  update(input: InputState, _state: GameState, dt: number): void {
    // Track pointer for hover effects
    this.hoverX = input.pointer.x;
    this.hoverY = input.pointer.y;
    this.globalTime += dt;

    // Auto-rotate direction unless user is manually turning
    if (!this.manualDir) {
      this.dirTimer += dt;
      if (this.dirTimer >= AUTO_ROTATE_SEC) {
        this.dirTimer -= AUTO_ROTATE_SEC;
        this.dirIndex = (this.dirIndex + 1) % DIRS.length;
        if (this.charState) this.charState.dirty = true;
        if (this.deviceCharState) this.deviceCharState.dirty = true;
      }
    }

    // Idle anim for preview
    if (this.charState) updateAnimation(this.charState, dt);

    // ── Attention engine — gaze tracking, blink, idle saccades, hair sway ──
    // Makes the preview feel alive: eyes track mouse, blink naturally,
    // hair gently sways, earrings/necklaces drift.
    {
      const attTargets: import("../character/attention.js").AttentionTarget[] = [];

      // Cursor gaze tracking — character watches the player's mouse
      if (this.hoverX !== 0 || this.hoverY !== 0) {
        const W = _state.canvasWidth;
        const H = _state.canvasHeight;
        const land = isLandscape(W, H);
        // Character center position in the preview layout
        const previewCx = land ? W * 0.28 : W * 0.5;
        const previewCy = land ? H * 0.40 : H * 0.18;
        const baseScale = Math.max(1.5, Math.min(3, Math.min(W, H) / 200));
        // Actual eye screen position: feet at cy+baseScale*68, eyes ~33px above feet
        // in character units, so eye screen Y = previewCy + baseScale*(68-33) = previewCy + baseScale*35
        const eyeScreenX = previewCx;
        const eyeScreenY = previewCy + baseScale * 35;

        // Compute gaze directly (don't use cursorToGaze which hardcodes -30 eye height)
        const dx = this.hoverX - eyeScreenX;
        const dy = this.hoverY - eyeScreenY;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const maxDist = 250;
        const t = Math.min(dist / maxDist, 1);
        const clamp = (v: number, lo: number, hi: number) => v < lo ? lo : v > hi ? hi : v;
        attTargets.push({
          type: "cursor",
          screenX: clamp((dx / dist) * t * 2, -2, 2),
          screenY: clamp((dy / dist) * t * 0.8, -1, 1),
          priority: 2,
        });
      }

      // Synthetic idle hair sway — gentle sine even when standing still
      this.idleHairPhase += dt;
      const idleHairSwing = Math.sin(this.idleHairPhase * 1.7) * 0.35
                          + Math.sin(this.idleHairPhase * 0.7) * 0.15;

      updateAttention(this.attentionState, dt, attTargets, 0, 0, false, 0, 0);

      // Override hair swing with gentle idle sway (attention engine produces 0 without velocity)
      this.attentionState.hairSwingX = idleHairSwing;
      // Subtle accessory drift
      this.attentionState.secondaryLagX = Math.sin(this.idleHairPhase * 1.3) * 0.2;
      this.attentionState.secondaryLagY = Math.sin(this.idleHairPhase * 0.9) * 0.08;
    }

    // Device preview tilt animation (smooth rotation for 3D effect)
    if (this.activeCategory === "devices") {
      this.devicePreviewAngle += dt * 0.8;
      // Keep device char state in sync with main appearance
      if (this.deviceCharState) {
        this.deviceCharState.appearance = { ...this.appearance, heldItem: this.deviceFocus === "phone" ? 6 : 7 };
        this.deviceCharState.animState = "device";
        updateAnimation(this.deviceCharState, dt);
      }
    }

    // Cursor blink
    this.cursorTimer += dt;
    if (this.cursorTimer >= 0.5) {
      this.cursorTimer -= 0.5;
      this.cursorVis = !this.cursorVis;
    }

    // Click / tap
    if (input.clickPosition) this.processClick(input.clickPosition);

    // Keyboard → name editing or category shortcuts
    if (this.nameEditing) {
      this.processNameKeys(input);
    } else {
      for (const key of input.keysJustPressed) {
        if (key === "ArrowLeft")  { this.rotateDirManual(-1); }
        if (key === "ArrowRight") { this.rotateDirManual(1); }
        if (key === "Tab") {
          const idx = CATEGORIES.findIndex(c => c.id === this.activeCategory);
          this.activeCategory = CATEGORIES[(idx + 1) % CATEGORIES.length].id;
          this.scrollY = 0;
        }
      }
    }

    // Scroll
    if (input.wheelZoomDelta !== 0) {
      this.scrollY = Math.max(0, Math.min(this.maxScrollY, this.scrollY - input.wheelZoomDelta * 120));
    }
  }

  // ─── Render ─────────────────────────────────────────────────────────────

  render(_timestamp: number, state: GameState, _quality: number): void {
    const { ctx } = this;
    const W = state.canvasWidth;
    const H = state.canvasHeight;
    const s = uiScale(W, H);
    const land = isLandscape(W, H);

    this.hits = [];
    this.tooltipText = "";

    if (!this.charState) {
      this.charState = createCharacter(this.appearance);
    }

    // Background
    this.drawBg(W, H);

    // Title
    this.drawTitle(W, s);

    if (land) {
      // ── Landscape: preview left, controls right ──
      const previewCx = W * 0.28;
      const previewCy = H * 0.40;
      this.drawPreview(previewCx, previewCy, W, H, s);

      const panelX = W * 0.52;
      const panelW = W * 0.44;
      this.drawCategoryTabs(panelX, 56 * s, panelW, s);
      this.drawAttributeRows(panelX, 56 * s + 38 * s, panelW, Math.max(0, H - 56 * s - 38 * s - 120 * s), s);

      this.drawNameField(W / 2, H - 90 * s, Math.min(280 * s, W * 0.35), 36 * s, s);
      this.drawBeginButton(W / 2, H - 42 * s, Math.min(220 * s, W * 0.28), 44 * s, s);
    } else {
      // ── Portrait: preview top, controls below ──
      // Give the preview more room: ~26% of height, then tabs start after
      const previewCx = W * 0.5;
      const previewCy = H * 0.18;
      this.drawPreview(previewCx, previewCy, W, H, s);

      const panelX = W * 0.06;
      const panelW = W * 0.88;
      // Push tabs further down to avoid overlap with multi-angle strip
      const tabY = Math.max(H * 0.44, previewCy + 180 * s);
      this.drawCategoryTabs(panelX, tabY, panelW, s);
      this.drawAttributeRows(panelX, tabY + 38 * s, panelW, Math.max(0, H - tabY - 38 * s - 130 * s), s);

      this.drawNameField(W / 2, H - 100 * s, Math.min(260 * s, W * 0.7), 36 * s, s);
      this.drawBeginButton(W / 2, H - 48 * s, Math.min(200 * s, W * 0.6), 44 * s, s);
    }

    // Tooltip overlay — rendered last, on top of everything
    if (this.tooltipText) {
      this.drawTooltip(W, s);
    }
  }

  // ─── Background ─────────────────────────────────────────────────────────

  private drawBg(W: number, H: number): void {
    const { ctx } = this;

    // Warm radial gradient
    const grad = ctx.createRadialGradient(W / 2, H * 0.35, 0, W / 2, H * 0.35, Math.max(W, H) * 0.85);
    grad.addColorStop(0, "#1e1814");
    grad.addColorStop(0.5, C.bgLight);
    grad.addColorStop(1, C.bg);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    // Subtle noise-like pattern via faint radial dots
    ctx.save();
    ctx.globalAlpha = 0.03;
    for (let i = 0; i < 6; i++) {
      const x = W * (0.15 + Math.sin(i * 1.7) * 0.35);
      const y = H * (0.2 + Math.cos(i * 2.3) * 0.3);
      const r = Math.min(W, H) * (0.15 + i * 0.04);
      const dot = ctx.createRadialGradient(x, y, 0, x, y, r);
      dot.addColorStop(0, "#c4956a");
      dot.addColorStop(1, "transparent");
      ctx.fillStyle = dot;
      ctx.fillRect(0, 0, W, H);
    }
    ctx.restore();

    // Bottom vignette
    const vig = ctx.createLinearGradient(0, H * 0.7, 0, H);
    vig.addColorStop(0, "transparent");
    vig.addColorStop(1, "rgba(0,0,0,0.3)");
    ctx.fillStyle = vig;
    ctx.fillRect(0, H * 0.7, W, H * 0.3);
  }

  // ─── Title ──────────────────────────────────────────────────────────────

  /** Category-specific guidance messages that feel warm and contextual */
  private getCategoryGuide(): string {
    switch (this.activeCategory) {
      case "body":    return "Shape the foundation \u2014 your body, your skin, your canvas.";
      case "hair":    return "Hair tells a story before you speak a word.";
      case "face":    return "The eyes are where others will look first.";
      case "outfit":  return "Dress for the world you want to live in.";
      case "extras":  return "The finishing touches that make you, you.";
      case "devices": return "Choose your companion devices \u2014 they go everywhere with you.";
      default:        return "Every choice is a small act of self-expression.";
    }
  }

  private drawTitle(W: number, s: number): void {
    const { ctx } = this;
    const fontSize = Math.round(22 * s);
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.shadowColor = C.warm;
    ctx.shadowBlur = 24 * s;
    ctx.font = `bold ${fontSize}px Georgia, serif`;
    ctx.fillStyle = C.accent;
    ctx.fillText("Create Your Character", W / 2, 32 * s);
    ctx.shadowBlur = 0;

    // Contextual subtitle that changes with the active category
    ctx.font = `italic ${Math.round(11 * s)}px Georgia, serif`;
    ctx.fillStyle = C.textDim;
    ctx.fillText(this.getCategoryGuide(), W / 2, 32 * s + fontSize * 0.9);
    ctx.restore();
  }

  // ─── Tooltip Overlay ─────────────────────────────────────────────────────

  private drawTooltip(W: number, s: number): void {
    const { ctx } = this;
    const fontSize = Math.round(10 * s);
    ctx.save();
    ctx.font = `${fontSize}px Georgia, serif`;
    const textW = ctx.measureText(this.tooltipText).width;
    const padH = 6 * s;
    const padW = 8 * s;
    const tipW = textW + padW * 2;
    const tipH = fontSize + padH * 2;

    // Position above the hovered element, clamped to screen
    let tx = Math.max(4, Math.min(W - tipW - 4, this.tooltipX));
    let ty = this.tooltipY - tipH - 2;
    if (ty < 4) ty = this.tooltipY + 20 * s; // flip below if too high

    // Background
    ctx.fillStyle = "#1e1810f0";
    ctx.shadowColor = "rgba(0,0,0,0.4)";
    ctx.shadowBlur = 8;
    this.roundRect(tx, ty, tipW, tipH, 5, 5, 5, 5);
    ctx.fill();
    ctx.shadowBlur = 0;

    // Border
    ctx.strokeStyle = C.accent;
    ctx.lineWidth = 0.8;
    ctx.globalAlpha = 0.5;
    this.roundRect(tx, ty, tipW, tipH, 5, 5, 5, 5);
    ctx.stroke();
    ctx.globalAlpha = 1;

    // Text
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillStyle = C.text;
    ctx.fillText(this.tooltipText, tx + padW, ty + tipH / 2);
    ctx.restore();
  }

  // ─── Preview ────────────────────────────────────────────────────────────

  private drawPreview(cx: number, cy: number, W: number, H: number, s: number): void {
    const { ctx, charState: char } = this;
    if (!char) return;

    const baseScale = Math.max(1.5, Math.min(3, Math.min(W, H) / 200));

    // Platform / pedestal
    ctx.save();
    ctx.fillStyle = "#1a150f";
    ctx.strokeStyle = C.panelBorder;
    ctx.lineWidth = 1;
    const pw = 100 * s;
    const ph = 16 * s;
    ctx.beginPath();
    ctx.ellipse(cx, cy + baseScale * 68, pw, ph, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.restore();

    // Warm spotlight on platform — breathes gently
    const spotBreathe = 0.18 + Math.sin(this.globalTime * 1.2) * 0.03;
    ctx.save();
    const spot = ctx.createRadialGradient(cx, cy + 10 * s, 0, cx, cy + 10 * s, 110 * s);
    spot.addColorStop(0, `rgba(196, 149, 106, ${spotBreathe})`);
    spot.addColorStop(0.6, `rgba(196, 149, 106, ${spotBreathe * 0.33})`);
    spot.addColorStop(1, "rgba(196, 149, 106, 0)");
    ctx.fillStyle = spot;
    ctx.beginPath();
    ctx.ellipse(cx, cy + 10 * s, 110 * s, 60 * s, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Character — on Devices tab, show them holding the device
    const dir = DIRS[this.dirIndex];
    const onDevicesTab = this.activeCategory === "devices";

    if (onDevicesTab) {
      // Ensure we have a device-pose char state
      if (!this.deviceCharState) {
        this.deviceCharState = createCharacter({ ...this.appearance, heldItem: this.deviceFocus === "phone" ? 6 : 7 });
        this.deviceCharState.animState = "device";
      }
      // Set device context so the held item renders with the chosen color and case
      const devColor = this.deviceFocus === "phone" ? this.phoneColor : this.padColor;
      setDeviceContext(devColor, this.deviceFocus);
      setDeviceCaseContext(null); // creation screen: no case equipped yet
      // Sync appearance + force device pose
      this.deviceCharState.appearance = { ...this.appearance, heldItem: this.deviceFocus === "phone" ? 6 : 7 };
      this.deviceCharState.animState = "device";
      this.deviceCharState.dirty = true;

      // Device screen glow on the character
      ctx.save();
      const glowY = cy + baseScale * 30;
      const glow = ctx.createRadialGradient(cx, glowY, 0, cx, glowY, 40 * s);
      glow.addColorStop(0, "rgba(180,200,255,0.08)");
      glow.addColorStop(1, "rgba(180,200,255,0)");
      ctx.fillStyle = glow;
      ctx.fillRect(cx - 40 * s, glowY - 40 * s, 80 * s, 80 * s);
      ctx.restore();

      this.pushAttentionContext();
      ctx.save();
      ctx.imageSmoothingEnabled = false;
      ctx.translate(cx, cy + baseScale * 68);
      ctx.scale(baseScale, baseScale);
      renderCharacter(ctx, this.deviceCharState, 0, 0, dir, this.deviceCharState.animFrame, this.attentionState.isBlinking, 0);
      ctx.restore();
      // Reset device context after rendering
      setDeviceContext("#3a3a3a", null);
      setDeviceCaseContext(null);
      this.resetAttentionContext();
    } else {
      this.pushAttentionContext();
      ctx.save();
      ctx.imageSmoothingEnabled = false;
      ctx.translate(cx, cy + baseScale * 68);
      ctx.scale(baseScale, baseScale);
      renderCharacter(ctx, char, 0, 0, dir, char.animFrame, this.attentionState.isBlinking, char.bobOffset);
      ctx.restore();
      this.resetAttentionContext();
    }

    // Direction turntable: < DIR >
    const labelY = cy + baseScale * 68 + 26 * s;
    const arrowGap = 50 * s;

    // Left turn arrow
    this.drawArrow(cx - arrowGap, labelY, "left", 8 * s);
    this.addHit(cx - arrowGap - 20 * s, labelY - 20 * s, 40 * s, 40 * s, () => this.rotateDirManual(-1));

    // Right turn arrow
    this.drawArrow(cx + arrowGap, labelY, "right", 8 * s);
    this.addHit(cx + arrowGap - 20 * s, labelY - 20 * s, 40 * s, 40 * s, () => this.rotateDirManual(1));

    // Direction name with subtle warm glow
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `${Math.round(11 * s)}px monospace`;
    const dirHovered = this.isHovered(cx - arrowGap, labelY - 20 * s, arrowGap * 2, 40 * s);
    if (dirHovered) {
      ctx.shadowColor = C.warm;
      ctx.shadowBlur = 8;
    }
    ctx.fillStyle = dirHovered ? C.accent : C.textDim;
    ctx.fillText(dir.toUpperCase(), cx, labelY);
    ctx.restore();

    // Multi-angle turnaround strip — positioned below turntable with clear gap
    this.drawMultiAnglePreview(cx, labelY + 32 * s, W, baseScale, s);
  }

  // ─── Multi-Angle Preview Strip ──────────────────────────────────────────

  private drawMultiAnglePreview(cx: number, topY: number, W: number, baseScale: number, s: number): void {
    const { ctx } = this;

    const PREVIEW_DIRS: Direction[] = ["s", "se", "e", "ne", "n"];
    const PREVIEW_LABELS = ["Front", "\u00BE", "Side", "\u00BE Back", "Back"];
    // Scale mini-views down to prevent overflow — 38% of main
    const miniScale = baseScale * 0.38;

    // Character height in mini-scale pixels (feet at origin, head ~68 px above)
    const charH = 68 * miniScale;
    // Total height of each mini-view slot: character + label gap
    const labelGap = 10 * s;
    const labelFontSz = Math.round(9 * s);
    const slotH = charH + labelGap + labelFontSz;

    // Spread views across available width (capped so they don't overflow)
    const maxRowW = Math.min(W * 0.82, 340 * s);
    const spacing = maxRowW / PREVIEW_DIRS.length;
    const rowStartX = cx - maxRowW / 2 + spacing / 2;

    // Foot baseline Y (characters are drawn with feet at translate origin)
    const feetY = topY + charH;

    // Current selected direction
    const currentDir = DIRS[this.dirIndex];
    const onDevicesTab = this.activeCategory === "devices";

    for (let i = 0; i < PREVIEW_DIRS.length; i++) {
      const dir = PREVIEW_DIRS[i];
      const mx = rowStartX + i * spacing;
      const isSelected = dir === currentDir;

      // Subtle warm spotlight under each mini-view (much dimmer than main)
      ctx.save();
      const spot = ctx.createRadialGradient(mx, feetY, 0, mx, feetY, 28 * s);
      spot.addColorStop(0, "rgba(196,149,106,0.07)");
      spot.addColorStop(1, "rgba(196,149,106,0)");
      ctx.fillStyle = spot;
      ctx.fillRect(mx - 30 * s, feetY - charH, 60 * s, charH + 8 * s);
      ctx.restore();

      // Highlight ring under selected direction
      if (isSelected) {
        ctx.save();
        ctx.strokeStyle = C.accent;
        ctx.globalAlpha = 0.55;
        ctx.lineWidth = 1.5 * s;
        ctx.beginPath();
        ctx.ellipse(mx, feetY + 3 * s, 16 * s, 4 * s, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      // Render character
      ctx.save();
      ctx.imageSmoothingEnabled = false;
      ctx.translate(mx, feetY);
      ctx.scale(miniScale, miniScale);

      this.pushAttentionContext();
      if (onDevicesTab && this.deviceCharState) {
        const devColor = this.deviceFocus === "phone" ? this.phoneColor : this.padColor;
        setDeviceContext(devColor, this.deviceFocus);
        renderCharacter(ctx, this.deviceCharState, 0, 0, dir, this.deviceCharState.animFrame, this.attentionState.isBlinking, 0);
        setDeviceContext("#3a3a3a", null);
      } else if (this.charState) {
        renderCharacter(ctx, this.charState, 0, 0, dir, this.charState.animFrame, this.attentionState.isBlinking, 0);
      }
      this.resetAttentionContext();

      ctx.restore();

      // Label
      const miniHovered = this.isHovered(mx - spacing / 2, topY, spacing, slotH + labelGap + labelFontSz);
      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.font = `${labelFontSz}px Georgia, serif`;
      ctx.fillStyle = isSelected ? C.accent : miniHovered ? C.accentHover : C.textDim;
      ctx.globalAlpha = isSelected ? 0.9 : miniHovered ? 0.8 : 0.55;
      ctx.fillText(PREVIEW_LABELS[i], mx, feetY + labelGap);
      ctx.restore();

      // Click mini-view to select that direction
      this.addHit(mx - spacing / 2, topY, spacing, slotH + labelGap + labelFontSz, () => {
        const targetIdx = DIRS.indexOf(dir);
        if (targetIdx >= 0 && targetIdx !== this.dirIndex) {
          this.dirIndex = targetIdx;
          this.manualDir = true;
          if (this.charState) this.charState.dirty = true;
          if (this.deviceCharState) this.deviceCharState.dirty = true;
          this.dirTimer = -5;
          setTimeout(() => { this.manualDir = false; }, 5000);
          this.playUIClick("dir");
        }
      });
    }
  }

  // ─── Category Tabs ──────────────────────────────────────────────────────

  private drawCategoryTabs(x: number, y: number, w: number, s: number): void {
    const { ctx } = this;
    const tabW = w / CATEGORIES.length;
    const tabH = 30 * s;
    const fontSize = Math.round(12 * s);

    for (let i = 0; i < CATEGORIES.length; i++) {
      const cat = CATEGORIES[i];
      const tx = x + i * tabW;
      const active = cat.id === this.activeCategory;
      const hovered = !active && this.isHovered(tx, y, tabW, tabH);

      // Tab background
      ctx.save();
      ctx.fillStyle = active ? C.catActive : hovered ? "#1e1810" : C.catInactive;
      ctx.globalAlpha = active ? 0.9 : hovered ? 0.75 : 0.5;
      this.roundRect(tx + 1, y, tabW - 2, tabH, 6, 6, 0, 0);
      ctx.fill();

      if (active) {
        // Active tab accent with subtle pulse
        const pulse = 1.0 + Math.sin(this.globalTime * 2) * 0.08;
        ctx.strokeStyle = C.accent;
        ctx.lineWidth = 1.5 * pulse;
        this.roundRect(tx + 1, y, tabW - 2, tabH, 6, 6, 0, 0);
        ctx.stroke();
      } else if (hovered) {
        ctx.strokeStyle = C.accentDim;
        ctx.lineWidth = 1;
        this.roundRect(tx + 1, y, tabW - 2, tabH, 6, 6, 0, 0);
        ctx.stroke();
      }
      ctx.restore();

      // Label
      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `${active ? "bold " : ""}${fontSize}px Georgia, serif`;
      ctx.fillStyle = active ? C.accent : hovered ? C.accentHover : C.textDim;
      ctx.fillText(cat.label, tx + tabW / 2, y + tabH / 2);
      ctx.restore();

      // Hit region for tab
      this.addHit(tx, y, tabW, tabH, () => {
        if (this.activeCategory !== cat.id) {
          this.activeCategory = cat.id;
          this.scrollY = 0;
          this.tabSwitchTime = this.globalTime;
          this.playUIClick("tab");
        }
      });
    }
  }

  // ─── Attribute Rows (scrollable within clip) ────────────────────────────

  private drawAttributeRows(x: number, y: number, w: number, maxH: number, s: number): void {
    // Devices tab has its own custom renderer
    if (this.activeCategory === "devices") {
      this.drawDevicesTab(x, y, w, maxH, s);
      return;
    }

    const { ctx } = this;
    const attrs = ATTRIBUTES.filter(a => a.category === this.activeCategory);
    const rowH = Math.max(38 * s, Math.min(50 * s, maxH / Math.max(attrs.length, 1)));
    const totalH = attrs.length * rowH;
    this.maxScrollY = Math.max(0, totalH - maxH);
    this.scrollY = Math.min(this.scrollY, this.maxScrollY);

    // Panel bg
    ctx.save();
    ctx.fillStyle = C.panel;
    this.roundRect(x - 8, y - 4, w + 16, maxH + 8, 8, 8, 8, 8);
    ctx.fill();
    ctx.strokeStyle = C.panelBorder;
    ctx.lineWidth = 1;
    this.roundRect(x - 8, y - 4, w + 16, maxH + 8, 8, 8, 8, 8);
    ctx.stroke();
    ctx.restore();

    // Clip
    ctx.save();
    ctx.beginPath();
    ctx.rect(x - 8, y - 4, w + 16, maxH + 8);
    ctx.clip();

    for (let i = 0; i < attrs.length; i++) {
      const attr = attrs[i];
      const rowY = y + i * rowH - this.scrollY + rowH / 2;

      // Skip if offscreen
      if (rowY < y - rowH || rowY > y + maxH + rowH) continue;

      // String fields — convert to index for the row display
      const val = attr.key === "expression"
        ? EXPRESSION_VALUES.indexOf(this.appearance.expression)
        : attr.key === "bodyArchetype1" ? ARCHETYPE_IDS.indexOf(this.appearance.bodyArchetype1)
        : attr.key === "bodyArchetype2" ? ARCHETYPE_IDS.indexOf(this.appearance.bodyArchetype2)
        : attr.key === "archetypeBlend" ? Math.round(this.appearance.archetypeBlend * 4)
        : attr.key === "bodyGender" ? GENDER_IDS.indexOf(this.appearance.bodyGender)
        : attr.key === "nude" ? (this.appearance.nude ? 1 : 0)
        : this.appearance[attr.key] as number;
      const fontSize = Math.round(13 * s);
      const arrowSz = 9 * s;
      const tapSz = minTap(s);

      // Row hover detection
      const rowHovered = this.isHovered(x, rowY - rowH / 2, w, rowH);

      // Subtle row highlight on hover
      if (rowHovered) {
        ctx.save();
        ctx.fillStyle = "#c4956a";
        ctx.globalAlpha = 0.04;
        ctx.fillRect(x - 4, rowY - rowH / 2, w + 8, rowH);
        ctx.restore();
      }

      // Value change animation pulse
      const isRecentlyChanged = this.lastChangedAttr === attr.key &&
        (this.globalTime - this.lastChangeTime) < 0.35;
      const changePulse = isRecentlyChanged
        ? 1.0 - (this.globalTime - this.lastChangeTime) / 0.35
        : 0;

      // Label — show tooltip on hover
      const labelW = 80 * s; // approximate label hit area
      const labelHovered = this.isHovered(x, rowY - rowH / 2, labelW, rowH);
      if (labelHovered && ATTR_TOOLTIPS[attr.key]) {
        this.tooltipText = ATTR_TOOLTIPS[attr.key];
        this.tooltipX = x + 4;
        this.tooltipY = rowY - rowH / 2 - 4;
      }

      ctx.save();
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.font = `${fontSize}px Georgia, serif`;
      ctx.fillStyle = rowHovered ? C.highlight : C.text;
      ctx.fillText(attr.label, x + 4, rowY);
      ctx.restore();

      const rEdge = x + w - 4;

      // Right arrow
      this.drawArrow(rEdge, rowY, "right", arrowSz);
      this.addHit(rEdge - tapSz / 2, rowY - tapSz / 2, tapSz, tapSz, () => this.adjustAttr(attr, 1));

      // Left arrow
      const lEdge = rEdge - 90 * s;
      this.drawArrow(lEdge, rowY, "left", arrowSz);
      this.addHit(lEdge - tapSz / 2, rowY - tapSz / 2, tapSz, tapSz, () => this.adjustAttr(attr, -1));

      // Value display between arrows
      const midX = (lEdge + rEdge) / 2;

      // Flash pulse behind value on change
      if (changePulse > 0) {
        ctx.save();
        ctx.fillStyle = C.warm;
        ctx.globalAlpha = changePulse * 0.15;
        this.roundRect(lEdge + 4 * s, rowY - 12 * s, rEdge - lEdge - 8 * s, 24 * s, 4, 4, 4, 4);
        ctx.fill();
        ctx.restore();
      }

      if (attr.palette) {
        const idx = val < 0 ? 0 : val % attr.palette.length;
        const color = attr.palette[idx] ?? "#888";
        const swW = Math.round(28 * s);
        const swH = Math.round(18 * s);
        ctx.save();
        if (changePulse > 0) {
          ctx.shadowColor = color;
          ctx.shadowBlur = 10 * changePulse;
        }
        ctx.fillStyle = color;
        this.roundRect(midX - swW / 2, rowY - swH / 2, swW, swH, 4, 4, 4, 4);
        ctx.fill();
        ctx.strokeStyle = C.swatchBorder;
        ctx.lineWidth = 1;
        this.roundRect(midX - swW / 2, rowY - swH / 2, swW, swH, 4, 4, 4, 4);
        ctx.stroke();
        ctx.restore();
      } else {
        let display: string;
        if (attr.valueLabels) {
          const vi = val - attr.min;   // -1 min → index 0 = "None", 0 → index 1 = first real item
          display = attr.valueLabels[vi] ?? String(val);
        } else {
          display = val === -1 ? "None" : String(val + 1);
        }
        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        const valScale = 1.0 + changePulse * 0.12;
        ctx.translate(midX, rowY);
        ctx.scale(valScale, valScale);
        ctx.font = `${Math.round(12 * s)}px Georgia, serif`;
        ctx.fillStyle = changePulse > 0 ? C.warm : C.highlight;
        const maxTextW = rEdge - lEdge - 10 * s;
        ctx.fillText(display, 0, 0, maxTextW);
        ctx.restore();
      }

      // Subtle row divider
      if (i < attrs.length - 1) {
        ctx.save();
        ctx.strokeStyle = C.panelBorder;
        ctx.globalAlpha = 0.3;
        ctx.beginPath();
        ctx.moveTo(x, rowY + rowH / 2);
        ctx.lineTo(x + w, rowY + rowH / 2);
        ctx.stroke();
        ctx.restore();
      }
    }

    ctx.restore(); // un-clip

    // Scroll indicators
    if (this.scrollY > 0) {
      ctx.save();
      ctx.textAlign = "center";
      ctx.font = `${Math.round(10 * s)}px monospace`;
      ctx.fillStyle = C.textDim;
      ctx.fillText("\u25B2", x + w / 2, y - 2);
      ctx.restore();
    }
    if (this.scrollY < this.maxScrollY) {
      ctx.save();
      ctx.textAlign = "center";
      ctx.font = `${Math.round(10 * s)}px monospace`;
      ctx.fillStyle = C.textDim;
      ctx.fillText("\u25BC", x + w / 2, y + maxH + 6);
      ctx.restore();
    }
  }

  // ─── Devices Tab ─────────────────────────────────────────────────────────
  //
  // A premium device customization experience. Both hPhone and hPad shown
  // with physical detail — camera bump, volume buttons, speaker grills,
  // live screen content, material finish, perspective tilt. The character
  // in the main preview holds the active device in real time.

  private drawDevicesTab(x: number, y: number, w: number, maxH: number, s: number): void {
    const { ctx } = this;

    // Panel bg — darker, more premium feel for device config
    ctx.save();
    const panelGrad = ctx.createLinearGradient(x, y, x, y + maxH);
    panelGrad.addColorStop(0, "#161210ee");
    panelGrad.addColorStop(1, "#0e0c0aee");
    ctx.fillStyle = panelGrad;
    this.roundRect(x - 8, y - 4, w + 16, maxH + 8, 8, 8, 8, 8);
    ctx.fill();
    ctx.strokeStyle = "#2a201880";
    ctx.lineWidth = 1;
    this.roundRect(x - 8, y - 4, w + 16, maxH + 8, 8, 8, 8, 8);
    ctx.stroke();
    ctx.restore();

    // Clip to panel
    ctx.save();
    ctx.beginPath();
    ctx.rect(x - 8, y - 4, w + 16, maxH + 8);
    ctx.clip();

    const pad = 10 * s;
    const innerX = x + pad;
    const innerW = w - pad * 2;
    const contentH = maxH - pad * 2;
    let curY = y + pad - this.scrollY;

    // ── Section 1: Device Selector — both devices side by side ──

    const sectionLabelSz = Math.round(10 * s);
    ctx.save();
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.font = `${sectionLabelSz}px Georgia, serif`;
    ctx.fillStyle = C.textDim;
    ctx.fillText("CHOOSE YOUR DEVICE", innerX, curY + sectionLabelSz / 2);
    ctx.restore();
    curY += sectionLabelSz + 8 * s;

    // Draw both devices side by side — phone on left (narrow portrait), pad on right (wide landscape)
    const deviceAreaH = 160 * s;
    const gap = 12 * s;
    const phoneAreaW = (innerW - gap) * 0.34;
    const padAreaW   = (innerW - gap) * 0.66;
    const phoneAreaX = innerX;
    const padAreaX   = innerX + phoneAreaW + gap;

    this.drawDeviceCard(phoneAreaX, curY, phoneAreaW, deviceAreaH, s, "phone");
    this.drawDeviceCard(padAreaX, curY, padAreaW, deviceAreaH, s, "pad");
    curY += deviceAreaH + 12 * s;

    // ── Section 2: Color + Finish label ──

    const activeColor = this.deviceFocus === "phone" ? this.phoneColor : this.padColor;
    const activeFinish = this.deviceFocus === "phone" ? this.phoneFinish : this.padFinish;
    const colorEntry = DEVICE_COLORS.find(c => c.hex === activeColor);
    const colorName = colorEntry?.name ?? "Custom";
    const deviceLabel = this.deviceFocus === "phone" ? "hPhone" : "hPad";

    // "Customizing hPhone — Espresso (Matte)" header
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `${Math.round(12 * s)}px Georgia, serif`;
    ctx.fillStyle = C.accent;
    ctx.fillText(`${deviceLabel} \u2014 ${colorName}`, innerX + innerW / 2, curY);
    ctx.restore();
    curY += 18 * s;

    // ── Section 3: Finish selector ──

    const finishKeys: DeviceFinish[] = ["matte", "glossy", "metallic"];
    const finishBtnW = Math.min(70 * s, (innerW - 8 * s) / 3);
    const finishTotalW = finishBtnW * 3 + 6 * s;
    const finishStartX = innerX + (innerW - finishTotalW) / 2;
    const finishH = 26 * s;

    for (let i = 0; i < 3; i++) {
      const fk = finishKeys[i];
      const fx = finishStartX + i * (finishBtnW + 3 * s);
      const active = fk === activeFinish;

      ctx.save();
      ctx.fillStyle = active ? "#2a201860" : "#12100a40";
      const r0 = i === 0 ? 5 : 0;
      const r1 = i === 2 ? 5 : 0;
      this.roundRect(fx, curY, finishBtnW, finishH, r0, r1, r1, r0);
      ctx.fill();
      ctx.strokeStyle = active ? C.accent : C.panelBorder;
      ctx.lineWidth = active ? 1.5 : 0.5;
      this.roundRect(fx, curY, finishBtnW, finishH, r0, r1, r1, r0);
      ctx.stroke();

      // Finish label
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `${active ? "bold " : ""}${Math.round(10 * s)}px Georgia, serif`;
      ctx.fillStyle = active ? C.highlight : C.textDim;
      ctx.fillText(FINISH_LABELS[fk], fx + finishBtnW / 2, curY + finishH / 2);
      ctx.restore();

      this.addHit(fx, curY, finishBtnW, finishH, () => {
        if (this.deviceFocus === "phone") this.phoneFinish = fk;
        else this.padFinish = fk;
      });
    }
    curY += finishH + 12 * s;

    // ── Section 4: Color group tabs ──

    const groups = [...new Set(DEVICE_COLORS.map(c => c.group))];
    const groupBtnW = Math.min(50 * s, (innerW + 2 * s) / groups.length);
    const groupTotalW = groupBtnW * groups.length;
    const groupStartX = innerX + (innerW - groupTotalW) / 2;
    const groupH = 22 * s;

    for (let i = 0; i < groups.length; i++) {
      const grp = groups[i];
      const gx = groupStartX + i * groupBtnW;
      const active = grp === this.deviceColorGroup;

      ctx.save();
      if (active) {
        ctx.fillStyle = C.catActive;
        ctx.globalAlpha = 0.8;
      }
      this.roundRect(gx, curY, groupBtnW, groupH, 4, 4, 4, 4);
      if (active) ctx.fill();
      ctx.globalAlpha = 1;

      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `${Math.round(9 * s)}px Georgia, serif`;
      ctx.fillStyle = active ? C.accent : C.textDim;
      ctx.fillText(grp, gx + groupBtnW / 2, curY + groupH / 2);
      ctx.restore();

      this.addHit(gx, curY, groupBtnW, groupH, () => { this.deviceColorGroup = grp; });
    }
    curY += groupH + 8 * s;

    // ── Section 5: Color swatch grid (filtered by group) ──

    const filteredColors = DEVICE_COLORS.filter(c => c.group === this.deviceColorGroup);
    const swatchSize = Math.round(30 * s);
    const swatchGap = Math.round(5 * s);
    const cols = Math.max(1, Math.floor((innerW + swatchGap) / (swatchSize + swatchGap)));
    const gridW = cols * (swatchSize + swatchGap) - swatchGap;
    const gridX = innerX + (innerW - gridW) / 2;

    for (let i = 0; i < filteredColors.length; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const sx = gridX + col * (swatchSize + swatchGap);
      const sy = curY + row * (swatchSize + swatchGap);

      const dc = filteredColors[i];
      const selected = dc.hex === activeColor;

      // Swatch with material finish visual
      ctx.save();
      if (selected) {
        ctx.shadowColor = dc.hex;
        ctx.shadowBlur = 10 * s;
      }

      // Base color
      ctx.fillStyle = dc.hex;
      this.roundRect(sx, sy, swatchSize, swatchSize, 5, 5, 5, 5);
      ctx.fill();

      // Finish overlay
      if (activeFinish === "glossy" || activeFinish === "metallic") {
        const finGrad = ctx.createLinearGradient(sx, sy, sx + swatchSize, sy + swatchSize);
        if (activeFinish === "glossy") {
          finGrad.addColorStop(0, "rgba(255,255,255,0.25)");
          finGrad.addColorStop(0.4, "rgba(255,255,255,0.05)");
          finGrad.addColorStop(0.6, "rgba(255,255,255,0.0)");
          finGrad.addColorStop(1, "rgba(255,255,255,0.1)");
        } else {
          finGrad.addColorStop(0, "rgba(255,255,255,0.35)");
          finGrad.addColorStop(0.2, "rgba(255,255,255,0.0)");
          finGrad.addColorStop(0.5, "rgba(255,255,255,0.15)");
          finGrad.addColorStop(0.8, "rgba(255,255,255,0.0)");
          finGrad.addColorStop(1, "rgba(255,255,255,0.2)");
        }
        ctx.fillStyle = finGrad;
        this.roundRect(sx, sy, swatchSize, swatchSize, 5, 5, 5, 5);
        ctx.fill();
      }

      ctx.shadowBlur = 0;
      ctx.strokeStyle = selected ? C.accent : "#3a2a1a50";
      ctx.lineWidth = selected ? 2 : 0.5;
      this.roundRect(sx, sy, swatchSize, swatchSize, 5, 5, 5, 5);
      ctx.stroke();
      ctx.restore();

      // Checkmark on selected
      if (selected) {
        ctx.save();
        const r = parseInt(dc.hex.slice(1, 3), 16);
        const g = parseInt(dc.hex.slice(3, 5), 16);
        const b = parseInt(dc.hex.slice(5, 7), 16);
        const lum = r * 0.299 + g * 0.587 + b * 0.114;
        ctx.strokeStyle = lum > 140 ? "#1a1a1a" : "#ffffff";
        ctx.lineWidth = 2.5 * s;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        const ccx = sx + swatchSize / 2;
        const ccy = sy + swatchSize / 2;
        const cs = swatchSize * 0.18;
        ctx.beginPath();
        ctx.moveTo(ccx - cs * 1.2, ccy);
        ctx.lineTo(ccx - cs * 0.2, ccy + cs);
        ctx.lineTo(ccx + cs * 1.2, ccy - cs);
        ctx.stroke();
        ctx.restore();
      }

      // Color name tooltip on hover area — using label below swatch
      this.addHit(sx, sy, swatchSize, swatchSize, () => {
        if (this.deviceFocus === "phone") this.phoneColor = dc.hex;
        else this.padColor = dc.hex;
        if (this.deviceCharState) this.deviceCharState.dirty = true;
      });
    }

    const swatchRows = Math.ceil(filteredColors.length / cols);
    curY += swatchRows * (swatchSize + swatchGap) + 8 * s;

    // ── Section 6: Model selector (greyed, future expansion) ──

    const modelY = curY;
    const modelH = 28 * s;

    ctx.save();
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.font = `${sectionLabelSz}px Georgia, serif`;
    ctx.fillStyle = C.textDim;
    ctx.fillText("MODEL", innerX, modelY + sectionLabelSz / 2);
    ctx.restore();
    curY += sectionLabelSz + 6 * s;

    // Model buttons — phone or pad models depending on focus
    const models = this.deviceFocus === "phone" ? PHONE_MODELS : PAD_MODELS;
    const activeModelIdx = this.deviceFocus === "phone" ? this.phoneModelIdx : this.padModelIdx;
    const modelBtnW = Math.min(90 * s, (innerW - 6 * s * models.length) / models.length);
    const modelTotalW = modelBtnW * models.length + 4 * s * (models.length - 1);
    const modelStartX = innerX + (innerW - modelTotalW) / 2;

    for (let i = 0; i < models.length; i++) {
      const mx = modelStartX + i * (modelBtnW + 4 * s);
      const isActive = i === activeModelIdx;
      const mdl = models[i];

      ctx.save();
      ctx.fillStyle = isActive ? "#2a201860" : "#12100a40";
      this.roundRect(mx, curY, modelBtnW, modelH, 5, 5, 5, 5);
      ctx.fill();
      ctx.strokeStyle = isActive ? C.accent : C.panelBorder;
      ctx.lineWidth = isActive ? 1.5 : 0.5;
      this.roundRect(mx, curY, modelBtnW, modelH, 5, 5, 5, 5);
      ctx.stroke();

      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `${isActive ? "bold " : ""}${Math.round(9 * s)}px Georgia, serif`;
      ctx.fillStyle = isActive ? C.highlight : C.textDim;
      ctx.fillText(mdl.label, mx + modelBtnW / 2, curY + modelH / 2 - 4 * s);
      // Dimensions subtitle
      ctx.font = `${Math.round(7 * s)}px Georgia, serif`;
      ctx.fillStyle = isActive ? C.accent : "#4a3a2a80";
      ctx.fillText(`${mdl.widthMM}×${mdl.heightMM}mm`, mx + modelBtnW / 2, curY + modelH / 2 + 5 * s);
      ctx.restore();

      // Hit region for model selection
      this.hits.push({ x: mx, y: curY, w: modelBtnW, h: modelH, action: () => {
        if (this.deviceFocus === "phone") this.phoneModelIdx = i;
        else this.padModelIdx = i;
      }});
    }
    curY += modelH + 14 * s;

    // ── Section 7: 4-Way Device Views ──

    ctx.save();
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.font = `${sectionLabelSz}px Georgia, serif`;
    ctx.fillStyle = C.textDim;
    ctx.fillText("DEVICE VIEWS", innerX, curY + sectionLabelSz / 2);
    ctx.restore();
    curY += sectionLabelSz + 8 * s;

    // Draw 4 rotated views of the focused device: front, ¾, side, back
    {
      const viewLabels = ["Front", "\u00BE", "Side", "Back"];
      const isPhone = this.deviceFocus === "phone";
      const col = isPhone ? this.phoneColor : this.padColor;
      const fin = isPhone ? this.phoneFinish : this.padFinish;
      const curModel = isPhone ? this.phoneModel : this.padModel;

      // Two-row layout for pad (cards are landscape-wide), single row for phone
      const viewGap = 4 * s;
      const viewCols = isPhone ? 4 : 2;
      const viewRows = isPhone ? 1 : 2;
      const viewW = (innerW - viewGap * (viewCols - 1)) / viewCols;
      // Fit the device into each view card with generous padding
      const cardPadX = isPhone ? 8 * s : 10 * s;
      const cardPadY = 10 * s;
      const fitS = deviceFitScale(curModel, viewW - cardPadX * 2, 100 * s - cardPadY * 2);
      const fitPx = modelPx(curModel, fitS);
      const viewH = Math.max(70 * s, fitPx.h + cardPadY * 2 + 4 * s);

      for (let v = 0; v < 4; v++) {
        const vc = v % viewCols;
        const vr = Math.floor(v / viewCols);
        const vx = innerX + vc * (viewW + viewGap);
        const vy = curY + vr * (viewH + viewGap);
        const vcx = vx + viewW / 2;
        const vcy = vy + viewH / 2;

        // Subtle card background
        ctx.save();
        ctx.fillStyle = v === 0 ? "#1a150f40" : "#0e0c0a25";
        this.roundRect(vx, vy, viewW, viewH, 5, 5, 5, 5);
        ctx.fill();

        // Clip to card bounds so nothing overflows
        this.roundRect(vx, vy, viewW, viewH, 5, 5, 5, 5);
        ctx.clip();

        ctx.translate(vcx, vcy);
        const tilt = Math.sin(this.devicePreviewAngle + v * 0.8) * 0.02;
        ctx.rotate(tilt);

        if (v === 0) {
          // Front view — full device with screen
          if (isPhone) this.drawPhoneDetail(0, 0, fitS, col, fin, true);
          else this.drawPadDetail(0, 0, fitS, col, fin, true);
        } else if (v === 1) {
          // ¾ view — slight skew
          ctx.transform(1, 0, -0.12, 1, 0, 0);
          if (isPhone) this.drawPhoneDetail(0, 0, fitS * 0.88, col, fin, false);
          else this.drawPadDetail(0, 0, fitS * 0.88, col, fin, false);
        } else if (v === 2) {
          // Side/edge view — uses real device depth
          const dkC = this.darkenHex(col, 0.8);
          const edgeW = fitPx.d;
          // Side view: phone shows height, pad shows height (shorter dim)
          const edgeH = fitPx.h;
          const edgeCr = Math.max(1, edgeW * 0.3);
          ctx.fillStyle = col;
          this.roundRect(-edgeW / 2, -edgeH / 2, edgeW, edgeH, edgeCr, edgeCr, edgeCr, edgeCr);
          ctx.fill();
          ctx.strokeStyle = dkC;
          ctx.lineWidth = 0.6;
          this.roundRect(-edgeW / 2, -edgeH / 2, edgeW, edgeH, edgeCr, edgeCr, edgeCr, edgeCr);
          ctx.stroke();
          // Volume button on side
          ctx.fillStyle = dkC;
          const sideBtn = Math.max(0.8, edgeW * 0.35);
          ctx.fillRect(-edgeW / 2 - sideBtn - 0.5, -edgeH * 0.15, sideBtn, edgeH * 0.08);
          ctx.fillRect(-edgeW / 2 - sideBtn - 0.5, -edgeH * 0.04, sideBtn, edgeH * 0.08);
          // Power button
          ctx.fillRect(edgeW / 2 + 0.5, -edgeH * 0.06, sideBtn, edgeH * 0.1);
        } else {
          // Back view — shell with camera, uses model dimensions
          const dkC = this.darkenHex(col, 0.8);
          const bw = fitPx.w;
          const bh = fitPx.h;
          const bcr = fitPx.cr;
          this.roundRect(-bw / 2, -bh / 2, bw, bh, bcr, bcr, bcr, bcr);
          ctx.fillStyle = col;
          ctx.fill();
          ctx.strokeStyle = dkC;
          ctx.lineWidth = 0.8;
          this.roundRect(-bw / 2, -bh / 2, bw, bh, bcr, bcr, bcr, bcr);
          ctx.stroke();

          // Camera module — proportional
          const camS = Math.min(bw, bh) * 0.08;
          const camOx = -bw * 0.3;
          const camOy = -bh * 0.32;
          ctx.fillStyle = "#1a1a1a";
          ctx.beginPath(); ctx.arc(camOx, camOy, camS, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = "#2a2a2a";
          ctx.beginPath(); ctx.arc(camOx, camOy, camS * 0.6, 0, Math.PI * 2); ctx.fill();
          // Flash
          ctx.fillStyle = "#ffe0a0";
          ctx.globalAlpha = 0.6;
          ctx.beginPath(); ctx.arc(camOx + camS * 2.5, camOy, camS * 0.4, 0, Math.PI * 2); ctx.fill();
          ctx.globalAlpha = 1;
          // Logo — centered
          const logoR = Math.min(bw, bh) * 0.06;
          const logoGrad = ctx.createRadialGradient(0, bh * 0.06, 0, 0, bh * 0.06, logoR * 1.3);
          logoGrad.addColorStop(0, dkC);
          logoGrad.addColorStop(1, col);
          ctx.fillStyle = logoGrad;
          ctx.beginPath(); ctx.arc(0, bh * 0.06, logoR, 0, Math.PI * 2); ctx.fill();
        }

        ctx.restore();

        // View label below card
        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        ctx.font = `${Math.round(8 * s)}px Georgia, serif`;
        ctx.fillStyle = C.textDim;
        ctx.fillText(viewLabels[v], vcx, vy + viewH + 3 * s);
        ctx.restore();
      }

      curY += viewRows * (viewH + viewGap) + 14 * s + 10 * s;
    }

    // ── Section 8: Stacked Product Boxes Display ──

    ctx.save();
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.font = `${sectionLabelSz}px Georgia, serif`;
    ctx.fillStyle = C.textDim;
    ctx.fillText("YOUR DEVICES", innerX, curY + sectionLabelSz / 2);
    ctx.restore();
    curY += sectionLabelSz + 10 * s;

    // Draw both product boxes stacked: hPad box at bottom (landscape, wider),
    // hPhone box angled and sitting on top — proportional to real device sizes
    {
      // Product boxes are slightly larger than the device inside (packaging margin ~15%)
      const pkgMargin = 1.15;
      const phMdl = this.phoneModel;
      const pdMdl = this.padModel;

      // Fit pad box to panel width (it's the wider box), phone scales proportionally
      const padBaseW = pdMdl.widthMM * pkgMargin;
      const padBaseH = pdMdl.heightMM * pkgMargin;
      const phoneBaseW = phMdl.widthMM * pkgMargin;
      const phoneBaseH = phMdl.heightMM * pkgMargin;

      // Scale everything so the pad box fits within panel with room to spare
      const boxScale = (innerW * 0.82) / (padBaseW * MM_TO_PX);
      const padBoxW = Math.round(padBaseW * MM_TO_PX * boxScale);
      const padBoxH = Math.round(padBaseH * MM_TO_PX * boxScale);
      const phoneBoxW = Math.round(phoneBaseW * MM_TO_PX * boxScale);
      const phoneBoxH = Math.round(phoneBaseH * MM_TO_PX * boxScale);

      const depth = 8 * s;
      const boxAreaH = padBoxH + phoneBoxH * 0.6 + depth + 20 * s;
      const boxCx = innerX + innerW / 2;
      const boxCy = curY + boxAreaH / 2;

      // Ambient glow behind the whole display
      ctx.save();
      const ambGlow = ctx.createRadialGradient(boxCx, boxCy, 0, boxCx, boxCy, innerW * 0.6);
      ambGlow.addColorStop(0, "rgba(196,149,106,0.06)");
      ambGlow.addColorStop(1, "rgba(196,149,106,0)");
      ctx.fillStyle = ambGlow;
      ctx.fillRect(innerX, curY, innerW, boxAreaH);
      ctx.restore();

      // ── Pad box — landscape, sits at the base ──
      const padBoxX = boxCx - padBoxW / 2;
      const padBoxY = curY + boxAreaH - padBoxH - 8 * s;

      ctx.save();
      const padBCol = this.padColor;
      const padTintR = Math.round(235 + (parseInt(padBCol.slice(1, 3), 16) - 235) * 0.1);
      const padTintG = Math.round(235 + (parseInt(padBCol.slice(3, 5), 16) - 235) * 0.1);
      const padTintB = Math.round(235 + (parseInt(padBCol.slice(5, 7), 16) - 235) * 0.1);
      const padFace = `rgb(${padTintR},${padTintG},${padTintB})`;
      const padSide = `rgb(${Math.round(padTintR * 0.85)},${Math.round(padTintG * 0.85)},${Math.round(padTintB * 0.85)})`;

      // Shadow
      ctx.fillStyle = "rgba(0,0,0,0.15)";
      ctx.beginPath();
      ctx.ellipse(boxCx, padBoxY + padBoxH + 4 * s, padBoxW * 0.45, 4 * s, 0, 0, Math.PI * 2);
      ctx.fill();

      // Box front face
      ctx.fillStyle = padFace;
      this.roundRect(padBoxX, padBoxY, padBoxW, padBoxH, 4, 4, 4, 4);
      ctx.fill();
      // Right side face (3D depth)
      ctx.fillStyle = padSide;
      ctx.beginPath();
      ctx.moveTo(padBoxX + padBoxW, padBoxY);
      ctx.lineTo(padBoxX + padBoxW + depth, padBoxY - depth * 0.5);
      ctx.lineTo(padBoxX + padBoxW + depth, padBoxY + padBoxH - depth * 0.5);
      ctx.lineTo(padBoxX + padBoxW, padBoxY + padBoxH);
      ctx.closePath();
      ctx.fill();
      // Top face
      const topFace = `rgb(${Math.min(255, padTintR + 8)},${Math.min(255, padTintG + 8)},${Math.min(255, padTintB + 8)})`;
      ctx.fillStyle = topFace;
      ctx.beginPath();
      ctx.moveTo(padBoxX, padBoxY);
      ctx.lineTo(padBoxX + depth, padBoxY - depth * 0.5);
      ctx.lineTo(padBoxX + padBoxW + depth, padBoxY - depth * 0.5);
      ctx.lineTo(padBoxX + padBoxW, padBoxY);
      ctx.closePath();
      ctx.fill();
      // Accent stripe
      ctx.fillStyle = padBCol;
      ctx.globalAlpha = 0.5;
      ctx.fillRect(padBoxX + 2, padBoxY + 2, 2.5 * s, padBoxH - 4);
      ctx.globalAlpha = 1;
      // Label + model name
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `300 ${Math.round(Math.max(10, padBoxH * 0.22))}px system-ui, sans-serif`;
      ctx.fillStyle = `rgba(${Math.round(padTintR * 0.45)},${Math.round(padTintG * 0.45)},${Math.round(padTintB * 0.45)},0.7)`;
      ctx.fillText(this.padModel.name, boxCx, padBoxY + padBoxH / 2);
      ctx.restore();

      // ── Phone box — portrait, taller and narrower, angled on top of pad box ──
      const phoneBoxX = boxCx - phoneBoxW / 2 + padBoxW * 0.12;
      const phoneBoxY = padBoxY - phoneBoxH * 0.55;

      ctx.save();
      ctx.translate(phoneBoxX + phoneBoxW / 2, phoneBoxY + phoneBoxH / 2);
      ctx.rotate(-0.08); // slight angle — casually leaning
      ctx.translate(-(phoneBoxX + phoneBoxW / 2), -(phoneBoxY + phoneBoxH / 2));

      const phBCol = this.phoneColor;
      const phTintR = Math.round(235 + (parseInt(phBCol.slice(1, 3), 16) - 235) * 0.1);
      const phTintG = Math.round(235 + (parseInt(phBCol.slice(3, 5), 16) - 235) * 0.1);
      const phTintB = Math.round(235 + (parseInt(phBCol.slice(5, 7), 16) - 235) * 0.1);
      const phFace = `rgb(${phTintR},${phTintG},${phTintB})`;
      const phSide = `rgb(${Math.round(phTintR * 0.85)},${Math.round(phTintG * 0.85)},${Math.round(phTintB * 0.85)})`;

      // Shadow
      ctx.fillStyle = "rgba(0,0,0,0.12)";
      ctx.beginPath();
      ctx.ellipse(phoneBoxX + phoneBoxW / 2, phoneBoxY + phoneBoxH + 3 * s, phoneBoxW * 0.4, 3 * s, 0, 0, Math.PI * 2);
      ctx.fill();

      // Front face
      ctx.fillStyle = phFace;
      this.roundRect(phoneBoxX, phoneBoxY, phoneBoxW, phoneBoxH, 4, 4, 4, 4);
      ctx.fill();
      // Right side
      const phDepth = 6 * s;
      ctx.fillStyle = phSide;
      ctx.beginPath();
      ctx.moveTo(phoneBoxX + phoneBoxW, phoneBoxY);
      ctx.lineTo(phoneBoxX + phoneBoxW + phDepth, phoneBoxY - phDepth * 0.5);
      ctx.lineTo(phoneBoxX + phoneBoxW + phDepth, phoneBoxY + phoneBoxH - phDepth * 0.5);
      ctx.lineTo(phoneBoxX + phoneBoxW, phoneBoxY + phoneBoxH);
      ctx.closePath();
      ctx.fill();
      // Top face
      const phTop = `rgb(${Math.min(255, phTintR + 8)},${Math.min(255, phTintG + 8)},${Math.min(255, phTintB + 8)})`;
      ctx.fillStyle = phTop;
      ctx.beginPath();
      ctx.moveTo(phoneBoxX, phoneBoxY);
      ctx.lineTo(phoneBoxX + phDepth, phoneBoxY - phDepth * 0.5);
      ctx.lineTo(phoneBoxX + phoneBoxW + phDepth, phoneBoxY - phDepth * 0.5);
      ctx.lineTo(phoneBoxX + phoneBoxW, phoneBoxY);
      ctx.closePath();
      ctx.fill();
      // Accent stripe
      ctx.fillStyle = phBCol;
      ctx.globalAlpha = 0.6;
      ctx.fillRect(phoneBoxX + 2, phoneBoxY + 2, 2 * s, phoneBoxH - 4);
      ctx.globalAlpha = 1;
      // Label + model name
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `300 ${Math.round(Math.max(9, phoneBoxW * 0.18))}px system-ui, sans-serif`;
      ctx.fillStyle = `rgba(${Math.round(phTintR * 0.45)},${Math.round(phTintG * 0.45)},${Math.round(phTintB * 0.45)},0.7)`;
      ctx.fillText(this.phoneModel.name, phoneBoxX + phoneBoxW / 2, phoneBoxY + phoneBoxH / 2);
      ctx.restore();

      curY += boxAreaH + 10 * s;
    }

    // ── Section 9: Description ──

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `italic ${Math.round(10 * s)}px Georgia, serif`;
    ctx.fillStyle = C.textDim;
    const desc = this.deviceFocus === "phone"
      ? "Your hPhone \u2014 always in your pocket. Press D anywhere to pull it out."
      : "Your hPad \u2014 a bigger canvas, held with both hands.";
    ctx.fillText(desc, innerX + innerW / 2, curY);
    ctx.restore();
    curY += 16 * s;

    // Track scroll
    this.maxScrollY = Math.max(0, (curY + this.scrollY) - (y + contentH));

    ctx.restore(); // un-clip
  }

  /** Draw a device card as a premium product box — unboxing energy, always. */
  private drawDeviceCard(
    x: number, y: number, w: number, h: number, s: number,
    type: "phone" | "pad"
  ): void {
    const { ctx } = this;
    const active  = this.deviceFocus === type;
    const color   = type === "phone" ? this.phoneColor : this.padColor;
    const finish  = type === "phone" ? this.phoneFinish : this.padFinish;
    const cx      = x + w / 2;
    const t       = this.devicePreviewAngle; // continuous animation timer

    // ── Parse device color into r/g/b for tinting ────────────────────────────
    const cR = parseInt(color.slice(1, 3), 16);
    const cG = parseInt(color.slice(3, 5), 16);
    const cB = parseInt(color.slice(5, 7), 16);

    // Box surface: almost white, faintly tinted by the device's chosen color
    const tintR = Math.round(240 + (cR - 240) * 0.12);
    const tintG = Math.round(240 + (cG - 240) * 0.12);
    const tintB = Math.round(240 + (cB - 240) * 0.12);
    const boxFace = `rgb(${tintR},${tintG},${tintB})`;
    const boxTop  = `rgb(${Math.min(255, tintR + 10)},${Math.min(255, tintG + 10)},${Math.min(255, tintB + 10)})`;
    const boxSide = `rgb(${Math.round(tintR * 0.88)},${Math.round(tintG * 0.88)},${Math.round(tintB * 0.88)})`;

    // ── Geometry ─────────────────────────────────────────────────────────────
    const boxW       = w * 0.72;
    const boxH       = h * 0.54;
    const boxX       = cx - boxW / 2;
    const boxBY      = y + h * 0.86 - boxH;   // top-left Y of box front face
    const isoDepthY  = boxH * 0.16;            // apparent height of top face
    const isoDepthX  = boxW * 0.06;            // apparent width of right side face
    const accentW    = 2.5 * s;

    // Lid is partially open — more open when active, breathes gently
    const lidLift    = (active ? boxH * 0.36 : boxH * 0.10)
                     + (active ? Math.sin(t * 0.9) * boxH * 0.04 : 0);
    const lidH       = boxH * 0.21;

    // ── Ambient glow behind card ─────────────────────────────────────────────
    if (active) {
      ctx.save();
      const glow = ctx.createRadialGradient(cx, y + h * 0.55, 0, cx, y + h * 0.55, w * 0.65);
      glow.addColorStop(0,   `rgba(${cR},${cG},${cB},0.13)`);
      glow.addColorStop(0.5, `rgba(${cR},${cG},${cB},0.05)`);
      glow.addColorStop(1,   "transparent");
      ctx.fillStyle = glow;
      ctx.fillRect(x, y, w, h);
      ctx.restore();
    }

    // ── Warm inner light spilling from open box ──────────────────────────────
    if (active) {
      ctx.save();
      const wl = ctx.createRadialGradient(cx, boxBY + boxH * 0.25, 0, cx, boxBY + boxH * 0.25, boxW * 0.7);
      wl.addColorStop(0,   `rgba(${Math.min(255, tintR + 20)},${Math.min(255, tintG + 15)},${Math.min(255, tintB)},0.22)`);
      wl.addColorStop(0.4, `rgba(${cR},${cG},${cB},0.08)`);
      wl.addColorStop(1,   "transparent");
      ctx.fillStyle = wl;
      ctx.fillRect(boxX - boxW * 0.3, boxBY - boxH * 0.6, boxW * 1.6, boxH * 1.2);
      ctx.restore();
    }

    // ── Ground shadow ellipse ────────────────────────────────────────────────
    ctx.save();
    ctx.globalAlpha = active ? 0.28 : 0.13;
    const sg = ctx.createRadialGradient(cx, boxBY + boxH + isoDepthY - 2 * s, 0, cx, boxBY + boxH + isoDepthY - 2 * s, boxW * 0.55);
    sg.addColorStop(0, "rgba(0,0,0,0.6)");
    sg.addColorStop(1, "transparent");
    ctx.fillStyle = sg;
    ctx.beginPath();
    ctx.ellipse(cx, boxBY + boxH + isoDepthY - 2 * s, boxW * 0.52, 5 * s, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.restore();

    // ════════════════════════════════════════════════════════════════════════
    // BOX BODY: right-side face → top face → front face
    // ════════════════════════════════════════════════════════════════════════
    ctx.save();

    // Right-side face
    ctx.beginPath();
    ctx.moveTo(boxX + boxW,             boxBY);
    ctx.lineTo(boxX + boxW + isoDepthX, boxBY - isoDepthY);
    ctx.lineTo(boxX + boxW + isoDepthX, boxBY + boxH - isoDepthY);
    ctx.lineTo(boxX + boxW,             boxBY + boxH);
    ctx.closePath();
    ctx.fillStyle = boxSide;
    ctx.fill();
    ctx.strokeStyle = "rgba(0,0,0,0.10)";
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // Top face
    ctx.beginPath();
    ctx.moveTo(boxX,                    boxBY);
    ctx.lineTo(boxX + isoDepthX,        boxBY - isoDepthY);
    ctx.lineTo(boxX + boxW + isoDepthX, boxBY - isoDepthY);
    ctx.lineTo(boxX + boxW,             boxBY);
    ctx.closePath();
    const tfg = ctx.createLinearGradient(boxX, boxBY - isoDepthY, boxX + boxW, boxBY);
    tfg.addColorStop(0, boxTop);
    tfg.addColorStop(1, boxSide);
    ctx.fillStyle = tfg;
    ctx.fill();
    ctx.strokeStyle = "rgba(0,0,0,0.07)";
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // Front face
    const ffg = ctx.createLinearGradient(boxX, boxBY, boxX + boxW, boxBY + boxH);
    ffg.addColorStop(0,    boxFace);
    ffg.addColorStop(0.65, boxFace);
    ffg.addColorStop(1,    `rgb(${Math.round(tintR * 0.94)},${Math.round(tintG * 0.94)},${Math.round(tintB * 0.94)})`);
    this.roundRect(boxX, boxBY, boxW, boxH, 2 * s, 2 * s, 3 * s, 3 * s);
    ctx.fillStyle = ffg;
    ctx.fill();

    // AO gradient at base of front face
    const aog = ctx.createLinearGradient(boxX, boxBY + boxH * 0.78, boxX, boxBY + boxH);
    aog.addColorStop(0, "rgba(0,0,0,0)");
    aog.addColorStop(1, "rgba(0,0,0,0.08)");
    this.roundRect(boxX, boxBY + boxH * 0.78, boxW, boxH * 0.22, 0, 0, 3 * s, 3 * s);
    ctx.fillStyle = aog;
    ctx.fill();

    // Front face border
    this.roundRect(boxX, boxBY, boxW, boxH, 2 * s, 2 * s, 3 * s, 3 * s);
    ctx.strokeStyle = "rgba(0,0,0,0.13)";
    ctx.lineWidth = 0.75;
    ctx.stroke();

    // Specular highlight along top edge of front
    ctx.beginPath();
    ctx.moveTo(boxX + 2 * s, boxBY + 0.4);
    ctx.lineTo(boxX + boxW - 2 * s, boxBY + 0.4);
    ctx.strokeStyle = "rgba(255,255,255,0.65)";
    ctx.lineWidth = 0.8;
    ctx.stroke();

    ctx.restore();

    // ── Accent color stripe — left edge of front face, device color ──────────
    ctx.save();
    this.roundRect(boxX, boxBY + boxH * 0.10, accentW, boxH * 0.80, 1, 0, 0, 1);
    ctx.fillStyle = color;
    ctx.globalAlpha = active ? 0.90 : 0.50;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.restore();

    // ── Typography on front face ─────────────────────────────────────────────
    const productName = type === "phone" ? "hPhone" : "hPad";
    const foilSweep   = (Math.sin(t * 0.4) + 1) * 0.5; // 0–1

    ctx.save();
    ctx.textAlign    = "left";
    ctx.textBaseline = "middle";
    ctx.font = `${Math.round(9 * s)}px -apple-system, "Helvetica Neue", Arial, sans-serif`;

    // Emboss shadow
    ctx.fillStyle = `rgba(${Math.round(tintR * 0.76)},${Math.round(tintG * 0.76)},${Math.round(tintB * 0.76)},0.85)`;
    ctx.fillText(productName, boxX + 6 * s + 0.5, boxBY + boxH * 0.27 + 0.5);

    // Foil fill — metallic sheen shifts with animation timer
    const tx  = boxX + 6 * s;
    const ty  = boxBY + boxH * 0.27;
    const tw  = ctx.measureText(productName).width;
    const fx0 = tx + tw * (foilSweep * 0.5);
    const fx1 = tx + tw * (foilSweep * 0.5 + 0.8);
    const fg  = ctx.createLinearGradient(fx0, ty - 6 * s, fx1, ty + 6 * s);
    fg.addColorStop(0,    `rgba(${Math.round(tintR * 0.60)},${Math.round(tintG * 0.60)},${Math.round(tintB * 0.60)},1)`);
    fg.addColorStop(0.35, `rgba(${Math.round(tintR * 0.70)},${Math.round(tintG * 0.70)},${Math.round(tintB * 0.70)},1)`);
    fg.addColorStop(0.50, `rgba(${Math.min(255, tintR + 20)},${Math.min(255, tintG + 20)},${Math.min(255, tintB + 20)},1)`);
    fg.addColorStop(0.65, `rgba(${Math.round(tintR * 0.70)},${Math.round(tintG * 0.70)},${Math.round(tintB * 0.70)},1)`);
    fg.addColorStop(1,    `rgba(${Math.round(tintR * 0.60)},${Math.round(tintG * 0.60)},${Math.round(tintB * 0.60)},1)`);
    ctx.fillStyle = fg;
    ctx.fillText(productName, tx, ty);

    // Tagline — barely there, very small
    ctx.font = `${Math.round(5.5 * s)}px -apple-system, "Helvetica Neue", Arial, sans-serif`;
    ctx.fillStyle = `rgba(${Math.round(tintR * 0.64)},${Math.round(tintG * 0.64)},${Math.round(tintB * 0.64)},0.8)`;
    ctx.fillText(
      type === "phone" ? "Always in your pocket." : "A bigger canvas.",
      tx, boxBY + boxH * 0.40
    );
    ctx.restore();

    // ════════════════════════════════════════════════════════════════════════
    // MOLDED INNER TRAY — dark insert, visible through the lifted lid gap
    // ════════════════════════════════════════════════════════════════════════
    const trayOpen = lidLift - lidH * 0.45;
    if (trayOpen > 2 * s) {
      ctx.save();
      // Clip to front face interior
      this.roundRect(boxX + 0.5, boxBY + 0.5, boxW - 1, boxH - 1, 2 * s, 2 * s, 3 * s, 3 * s);
      ctx.clip();

      const ti  = 6 * s;
      const trX = boxX + ti;
      const trY = boxBY + 2 * s;
      const trW = boxW - ti * 2;
      const trH = Math.min(trayOpen - 2 * s, boxH * 0.88);

      // Dark molded cardboard texture
      const tdR = Math.round(cR * 0.10 + 18);
      const tdG = Math.round(cG * 0.10 + 14);
      const tdB = Math.round(cB * 0.10 + 12);
      const trg = ctx.createLinearGradient(trX, trY, trX, trY + trH);
      trg.addColorStop(0,   `rgb(${tdR + 8},${tdG + 6},${tdB + 5})`);
      trg.addColorStop(0.4, `rgb(${tdR},${tdG},${tdB})`);
      trg.addColorStop(1,   `rgb(${Math.round(tdR * 0.78)},${Math.round(tdG * 0.78)},${Math.round(tdB * 0.78)})`);
      this.roundRect(trX, trY, trW, trH, 2 * s, 2 * s, 2 * s, 2 * s);
      ctx.fillStyle = trg;
      ctx.fill();

      // Ridge lip edge
      const ri = 4 * s;
      this.roundRect(trX + ri, trY + ri * 0.5, trW - ri * 2, Math.max(4 * s, trH - ri), 2 * s, 2 * s, 2 * s, 2 * s);
      ctx.strokeStyle = `rgba(${Math.min(255, tdR + 22)},${Math.min(255, tdG + 18)},${Math.min(255, tdB + 15)},0.65)`;
      ctx.lineWidth = 1;
      ctx.stroke();

      // Color-tinted sheen reflecting device color into tray
      const sh = ctx.createRadialGradient(trX + trW * 0.5, trY + trH * 0.3, 0, trX + trW * 0.5, trY + trH * 0.3, trW * 0.5);
      sh.addColorStop(0, `rgba(${cR},${cG},${cB},0.11)`);
      sh.addColorStop(1, "transparent");
      this.roundRect(trX, trY, trW, trH, 2 * s, 2 * s, 2 * s, 2 * s);
      ctx.fillStyle = sh;
      ctx.fill();

      ctx.restore();
    }

    // ════════════════════════════════════════════════════════════════════════
    // LID — floats above, partially lifted, casts shadow on box body
    // ════════════════════════════════════════════════════════════════════════
    const lidY = boxBY - lidLift;

    ctx.save();
    ctx.shadowColor   = "rgba(0,0,0,0.20)";
    ctx.shadowBlur    = 6 * s;
    ctx.shadowOffsetY = 3 * s;

    // Lid right-side face
    ctx.beginPath();
    ctx.moveTo(boxX + boxW,             lidY);
    ctx.lineTo(boxX + boxW + isoDepthX, lidY - isoDepthY);
    ctx.lineTo(boxX + boxW + isoDepthX, lidY + lidH - isoDepthY);
    ctx.lineTo(boxX + boxW,             lidY + lidH);
    ctx.closePath();
    ctx.fillStyle = boxSide;
    ctx.fill();

    // Lid top face
    ctx.beginPath();
    ctx.moveTo(boxX,                    lidY);
    ctx.lineTo(boxX + isoDepthX,        lidY - isoDepthY);
    ctx.lineTo(boxX + boxW + isoDepthX, lidY - isoDepthY);
    ctx.lineTo(boxX + boxW,             lidY);
    ctx.closePath();
    const ltg = ctx.createLinearGradient(boxX, lidY - isoDepthY, boxX + boxW, lidY);
    ltg.addColorStop(0, `rgb(${Math.min(255, tintR + 14)},${Math.min(255, tintG + 14)},${Math.min(255, tintB + 14)})`);
    ltg.addColorStop(1, boxSide);
    ctx.fillStyle = ltg;
    ctx.fill();

    // Lid front face
    const lfg = ctx.createLinearGradient(boxX, lidY, boxX + boxW, lidY + lidH);
    lfg.addColorStop(0, `rgb(${Math.min(255, tintR + 8)},${Math.min(255, tintG + 8)},${Math.min(255, tintB + 8)})`);
    lfg.addColorStop(1, boxFace);
    this.roundRect(boxX, lidY, boxW, lidH, 2 * s, 2 * s, 1 * s, 1 * s);
    ctx.fillStyle = lfg;
    ctx.fill();

    ctx.shadowBlur    = 0;
    ctx.shadowOffsetY = 0;

    this.roundRect(boxX, lidY, boxW, lidH, 2 * s, 2 * s, 1 * s, 1 * s);
    ctx.strokeStyle = "rgba(0,0,0,0.11)";
    ctx.lineWidth = 0.75;
    ctx.stroke();

    // Lid specular highlight
    ctx.beginPath();
    ctx.moveTo(boxX + 2 * s, lidY + 0.4);
    ctx.lineTo(boxX + boxW - 2 * s, lidY + 0.4);
    ctx.strokeStyle = "rgba(255,255,255,0.70)";
    ctx.lineWidth = 0.8;
    ctx.stroke();

    // Lid accent stripe — device color on lid too
    this.roundRect(boxX, lidY + lidH * 0.10, accentW, lidH * 0.80, 1, 0, 0, 1);
    ctx.fillStyle = color;
    ctx.globalAlpha = active ? 0.85 : 0.45;
    ctx.fill();
    ctx.globalAlpha = 1;

    ctx.restore();

    // ════════════════════════════════════════════════════════════════════════
    // DEVICE — nestled in tray, peeking out through the open lid gap
    // ════════════════════════════════════════════════════════════════════════
    const devVis = Math.max(0, Math.min(1, (trayOpen - 4 * s) / (boxH * 0.28)));
    if (devVis > 0) {
      ctx.save();
      ctx.globalAlpha = devVis * (active ? 1.0 : 0.65);

      // Clip so device only shows within box + above card top (not below box bottom)
      ctx.beginPath();
      ctx.rect(boxX - 2 * s, y, boxW + 4 * s + isoDepthX, boxBY + boxH + 1);
      ctx.clip();

      // Device center: rests in tray, rises slightly as lid lifts
      const devCX = cx;
      const devCY = boxBY + boxH * 0.12 - (lidLift - lidH) * 0.52;
      const tilt  = active ? Math.sin(t * 0.7) * 0.03 : 0;

      ctx.translate(devCX, devCY);
      ctx.rotate(tilt);

      // Fit device proportionally into the tray area
      const trayMaxW = boxW * 0.85;
      const trayMaxH = boxH * 0.75;
      const devModel = type === "phone" ? this.phoneModel : this.padModel;
      const ds = deviceFitScale(devModel, trayMaxW, trayMaxH);
      if (type === "phone") {
        this.drawPhoneDetail(0, 0, ds, color, finish, active);
      } else {
        this.drawPadDetail(0, 0, ds, color, finish, active);
      }

      ctx.restore();
    }

    // ── Selection rings when active ──────────────────────────────────────────
    if (active) {
      ctx.save();
      this.roundRect(x + 1, y + 1, w - 2, h - 2, 6, 6, 6, 6);
      ctx.strokeStyle = `rgba(${cR},${cG},${cB},0.44)`;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      this.roundRect(x + 3, y + 3, w - 6, h - 6, 5, 5, 5, 5);
      ctx.strokeStyle = `rgba(${cR},${cG},${cB},0.14)`;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.restore();
    }

    // ── Hit region — tap to focus this device ─────────────────────────────────
    this.addHit(x, y, w, h, () => {
      this.deviceFocus = type;
      if (this.deviceCharState) {
        this.deviceCharState.appearance = { ...this.appearance, heldItem: type === "phone" ? 6 : 7 };
        this.deviceCharState.dirty = true;
      }
    });
  }

  /** Draw a detailed hPhone (portrait) at local origin with full physical detail */
  private drawPhoneDetail(ox: number, oy: number, s: number, color: string, finish: DeviceFinish, active: boolean): void {
    const { ctx } = this;
    const pm = modelPx(this.phoneModel, s);
    const dw = pm.w;
    const dh = pm.h;
    const dx = ox - dw / 2;
    const dy = oy - dh / 2;
    const cr = pm.cr;

    // Drop shadow
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.4)";
    ctx.shadowBlur = dw * 0.2;
    ctx.shadowOffsetY = dh * 0.04;
    ctx.fillStyle = color;
    this.roundRect(dx, dy, dw, dh, cr, cr, cr, cr);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;
    ctx.restore();

    // Shell body with finish
    ctx.save();
    ctx.fillStyle = color;
    this.roundRect(dx, dy, dw, dh, cr, cr, cr, cr);
    ctx.fill();

    // Material finish treatment
    if (finish === "glossy") {
      const gloss = ctx.createLinearGradient(dx, dy, dx + dw * 0.7, dy + dh);
      gloss.addColorStop(0, "rgba(255,255,255,0.22)");
      gloss.addColorStop(0.3, "rgba(255,255,255,0.04)");
      gloss.addColorStop(0.7, "rgba(255,255,255,0.0)");
      gloss.addColorStop(1, "rgba(255,255,255,0.08)");
      ctx.fillStyle = gloss;
      this.roundRect(dx, dy, dw, dh, cr, cr, cr, cr);
      ctx.fill();
    } else if (finish === "metallic") {
      const met = ctx.createLinearGradient(dx, dy, dx + dw, dy + dh);
      met.addColorStop(0, "rgba(255,255,255,0.3)");
      met.addColorStop(0.15, "rgba(255,255,255,0.0)");
      met.addColorStop(0.45, "rgba(255,255,255,0.12)");
      met.addColorStop(0.55, "rgba(255,255,255,0.0)");
      met.addColorStop(0.85, "rgba(255,255,255,0.08)");
      met.addColorStop(1, "rgba(255,255,255,0.2)");
      ctx.fillStyle = met;
      this.roundRect(dx, dy, dw, dh, cr, cr, cr, cr);
      ctx.fill();
    }

    // Bezel edge — thin dark line
    ctx.strokeStyle = "rgba(0,0,0,0.35)";
    ctx.lineWidth = 1;
    this.roundRect(dx, dy, dw, dh, cr, cr, cr, cr);
    ctx.stroke();

    // Top edge highlight
    ctx.strokeStyle = "rgba(255,255,255,0.15)";
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(dx + cr, dy + 0.5);
    ctx.lineTo(dx + dw - cr, dy + 0.5);
    ctx.stroke();
    ctx.restore();

    // Volume buttons (left side) — proportional to device height
    const btnW = Math.max(1, dw * 0.04);
    const btnLen = dh * 0.07;
    ctx.save();
    ctx.fillStyle = this.shadeColor(color, 0.85);
    this.roundRect(dx - btnW, dy + dh * 0.2, btnW, btnLen, 0.5, 0, 0, 0.5);
    ctx.fill();
    this.roundRect(dx - btnW, dy + dh * 0.3, btnW, btnLen, 0.5, 0, 0, 0.5);
    ctx.fill();
    ctx.restore();

    // Power button (right side)
    ctx.save();
    ctx.fillStyle = this.shadeColor(color, 0.85);
    this.roundRect(dx + dw, dy + dh * 0.26, btnW, dh * 0.11, 0, 0.5, 0.5, 0);
    ctx.fill();
    ctx.restore();

    // Camera bump (top-left back — visible as subtle raised area)
    const camR = dw * 0.09;
    const camX = dx + dw * 0.21;
    const camY = dy + dw * 0.21;
    ctx.save();
    ctx.fillStyle = this.shadeColor(color, 0.9);
    ctx.beginPath();
    ctx.arc(camX, camY, camR, 0, Math.PI * 2);
    ctx.fill();
    // Camera lens
    ctx.fillStyle = "#1a1a2a";
    ctx.beginPath();
    ctx.arc(camX, camY, camR * 0.58, 0, Math.PI * 2);
    ctx.fill();
    // Lens reflection
    ctx.fillStyle = "rgba(100,120,200,0.4)";
    ctx.beginPath();
    ctx.arc(camX - camR * 0.15, camY - camR * 0.15, camR * 0.22, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Screen — proportional inset from bezel
    const si = pm.inset;
    const scrX = dx + si;
    const scrY = dy + si + dh * 0.055; // extra top for notch/dynamic island
    const scrW = dw - si * 2;
    const scrH = dh - si * 2 - dh * 0.085;

    ctx.save();
    ctx.fillStyle = "#0a0a14";
    this.roundRect(scrX, scrY, scrW, scrH, 2.5, 2.5, 2.5, 2.5);
    ctx.fill();

    // Mini status bar
    ctx.fillStyle = "#ffffff20";
    ctx.fillRect(scrX + scrW * 0.05, scrY + scrH * 0.02, scrW * 0.9, Math.max(0.5, scrH * 0.008));

    // Mini app icon grid on screen (4 icons, 2x2)
    if (active) {
      const iconS = scrW * 0.12;
      const iconGap = scrW * 0.08;
      const gridStartX = scrX + (scrW - iconS * 2 - iconGap) / 2;
      const gridStartY = scrY + scrH * 0.25;
      const iconColors = ["#ff6b35", "#22c55e", "#7c3aed", "#f59e0b"];
      for (let row = 0; row < 2; row++) {
        for (let col = 0; col < 2; col++) {
          const ix = gridStartX + col * (iconS + iconGap);
          const iy = gridStartY + row * (iconS + iconGap);
          ctx.fillStyle = iconColors[row * 2 + col];
          this.roundRect(ix, iy, iconS, iconS, iconS * 0.22, iconS * 0.22, iconS * 0.22, iconS * 0.22);
          ctx.fill();
        }
      }
    }

    // Screen glint
    ctx.globalAlpha = 0.12;
    const screenGlint = ctx.createLinearGradient(scrX, scrY, scrX + scrW, scrY + scrH);
    screenGlint.addColorStop(0, "#ffffff");
    screenGlint.addColorStop(0.15, "transparent");
    screenGlint.addColorStop(0.85, "transparent");
    screenGlint.addColorStop(1, "#ffffff40");
    ctx.fillStyle = screenGlint;
    const scrCr = Math.max(2, scrW * 0.04);
    this.roundRect(scrX, scrY, scrW, scrH, scrCr, scrCr, scrCr, scrCr);
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.restore();

    // Home bar
    ctx.save();
    ctx.fillStyle = "#ffffff25";
    const barW = dw * 0.32;
    const barH = Math.max(1, dh * 0.02);
    this.roundRect(ox - barW / 2, dy + dh - si - barH * 2, barW, barH, barH / 2, barH / 2, barH / 2, barH / 2);
    ctx.fill();
    ctx.restore();

    // Speaker grille (top center, above screen)
    ctx.save();
    ctx.fillStyle = "#00000030";
    const spkW = dw * 0.21;
    const spkH = Math.max(0.8, dh * 0.014);
    this.roundRect(ox - spkW / 2, dy + si + dh * 0.012, spkW, spkH, spkH / 2, spkH / 2, spkH / 2, spkH / 2);
    ctx.fill();
    ctx.restore();

    // Active glow behind device
    if (active) {
      ctx.save();
      ctx.globalCompositeOperation = "destination-over";
      const glow = ctx.createRadialGradient(ox, oy, dw * 0.3, ox, oy, dw);
      glow.addColorStop(0, color + "20");
      glow.addColorStop(1, "transparent");
      ctx.fillStyle = glow;
      ctx.fillRect(ox - dw, oy - dh, dw * 2, dh * 2);
      ctx.globalCompositeOperation = "source-over";
      ctx.restore();
    }
  }

  /** Draw a detailed hPad (landscape) at local origin with full physical detail */
  private drawPadDetail(ox: number, oy: number, s: number, color: string, finish: DeviceFinish, active: boolean): void {
    const { ctx } = this;
    const pdm = modelPx(this.padModel, s);
    // Pad model widthMM is the wider (landscape) dimension, heightMM is shorter
    const dw = pdm.w;
    const dh = pdm.h;
    const dx = ox - dw / 2;
    const dy = oy - dh / 2;
    const cr = pdm.cr;

    // Drop shadow
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.4)";
    ctx.shadowBlur = dw * 0.14;
    ctx.shadowOffsetY = dh * 0.06;
    ctx.fillStyle = color;
    this.roundRect(dx, dy, dw, dh, cr, cr, cr, cr);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;
    ctx.restore();

    // Shell body with finish
    ctx.save();
    ctx.fillStyle = color;
    this.roundRect(dx, dy, dw, dh, cr, cr, cr, cr);
    ctx.fill();

    if (finish === "glossy") {
      const gloss = ctx.createLinearGradient(dx, dy, dx + dw * 0.6, dy + dh);
      gloss.addColorStop(0, "rgba(255,255,255,0.22)");
      gloss.addColorStop(0.3, "rgba(255,255,255,0.04)");
      gloss.addColorStop(0.7, "rgba(255,255,255,0.0)");
      gloss.addColorStop(1, "rgba(255,255,255,0.08)");
      ctx.fillStyle = gloss;
      this.roundRect(dx, dy, dw, dh, cr, cr, cr, cr);
      ctx.fill();
    } else if (finish === "metallic") {
      const met = ctx.createLinearGradient(dx, dy, dx + dw, dy + dh);
      met.addColorStop(0, "rgba(255,255,255,0.3)");
      met.addColorStop(0.15, "rgba(255,255,255,0.0)");
      met.addColorStop(0.45, "rgba(255,255,255,0.12)");
      met.addColorStop(0.55, "rgba(255,255,255,0.0)");
      met.addColorStop(0.85, "rgba(255,255,255,0.08)");
      met.addColorStop(1, "rgba(255,255,255,0.2)");
      ctx.fillStyle = met;
      this.roundRect(dx, dy, dw, dh, cr, cr, cr, cr);
      ctx.fill();
    }

    // Bezel
    ctx.strokeStyle = "rgba(0,0,0,0.35)";
    ctx.lineWidth = 1;
    this.roundRect(dx, dy, dw, dh, cr, cr, cr, cr);
    ctx.stroke();

    // Top edge highlight
    ctx.strokeStyle = "rgba(255,255,255,0.15)";
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(dx + cr, dy + 0.5);
    ctx.lineTo(dx + dw - cr, dy + 0.5);
    ctx.stroke();
    ctx.restore();

    // Volume buttons (top side for landscape pad) — proportional
    const pbtnH = Math.max(1, dh * 0.04);
    const pbtnLen = dw * 0.04;
    ctx.save();
    ctx.fillStyle = this.shadeColor(color, 0.85);
    this.roundRect(dx + dw * 0.08, dy - pbtnH, pbtnLen, pbtnH, 0.5, 0.5, 0, 0);
    ctx.fill();
    this.roundRect(dx + dw * 0.08 + pbtnLen + dw * 0.02, dy - pbtnH, pbtnLen, pbtnH, 0.5, 0.5, 0, 0);
    ctx.fill();
    ctx.restore();

    // Power button (top-right)
    ctx.save();
    ctx.fillStyle = this.shadeColor(color, 0.85);
    this.roundRect(dx + dw * 0.88, dy - pbtnH, dw * 0.06, pbtnH, 0.5, 0.5, 0, 0);
    ctx.fill();
    ctx.restore();

    // Camera (top-left corner) — proportional
    const pcamR = dh * 0.08;
    const pcamX = dx + dh * 0.18;
    const pcamY = dy + dh * 0.18;
    ctx.save();
    ctx.fillStyle = this.shadeColor(color, 0.9);
    ctx.beginPath();
    ctx.arc(pcamX, pcamY, pcamR, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#1a1a2a";
    ctx.beginPath();
    ctx.arc(pcamX, pcamY, pcamR * 0.62, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "rgba(100,120,200,0.4)";
    ctx.beginPath();
    ctx.arc(pcamX - pcamR * 0.12, pcamY - pcamR * 0.12, pcamR * 0.25, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Screen — proportional inset
    const psi = pdm.inset;
    const scrX = dx + psi + dw * 0.02;
    const scrY = dy + psi;
    const scrW = dw - psi * 2 - dw * 0.04;
    const scrH = dh - psi * 2;

    ctx.save();
    ctx.fillStyle = "#0a0a14";
    this.roundRect(scrX, scrY, scrW, scrH, 3, 3, 3, 3);
    ctx.fill();

    // Mini status bar
    ctx.fillStyle = "#ffffff20";
    ctx.fillRect(scrX + scrW * 0.03, scrY + scrH * 0.02, scrW * 0.94, Math.max(0.5, scrH * 0.01));

    // Mini app icon grid (6 icons, 3x2)
    if (active) {
      const iconS = scrW * 0.065;
      const iconGap = scrW * 0.04;
      const gridStartX = scrX + (scrW - iconS * 3 - iconGap * 2) / 2;
      const gridStartY = scrY + scrH * 0.2;
      const iconColors = ["#ff6b35", "#22c55e", "#7c3aed", "#f59e0b", "#ec4899", "#6366f1"];
      for (let row = 0; row < 2; row++) {
        for (let col = 0; col < 3; col++) {
          const ix = gridStartX + col * (iconS + iconGap);
          const iy = gridStartY + row * (iconS + iconGap * 1.5);
          ctx.fillStyle = iconColors[row * 3 + col];
          const ir = iconS * 0.22;
          this.roundRect(ix, iy, iconS, iconS, ir, ir, ir, ir);
          ctx.fill();
        }
      }
    }

    // Screen glint
    ctx.globalAlpha = 0.12;
    const screenGlint = ctx.createLinearGradient(scrX, scrY, scrX + scrW, scrY + scrH);
    screenGlint.addColorStop(0, "#ffffff");
    screenGlint.addColorStop(0.15, "transparent");
    screenGlint.addColorStop(0.85, "transparent");
    screenGlint.addColorStop(1, "#ffffff40");
    ctx.fillStyle = screenGlint;
    const pScrCr = Math.max(2, scrW * 0.025);
    this.roundRect(scrX, scrY, scrW, scrH, pScrCr, pScrCr, pScrCr, pScrCr);
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.restore();

    // Home bar (bottom center)
    ctx.save();
    ctx.fillStyle = "#ffffff25";
    const pBarW = dw * 0.12;
    const pBarH = Math.max(1, dh * 0.025);
    this.roundRect(ox - pBarW / 2, dy + dh - psi - pBarH * 1.5, pBarW, pBarH, pBarH / 2, pBarH / 2, pBarH / 2, pBarH / 2);
    ctx.fill();
    ctx.restore();

    // Active glow
    if (active) {
      ctx.save();
      ctx.globalCompositeOperation = "destination-over";
      const glow = ctx.createRadialGradient(ox, oy, dw * 0.3, ox, oy, dw * 0.8);
      glow.addColorStop(0, color + "18");
      glow.addColorStop(1, "transparent");
      ctx.fillStyle = glow;
      ctx.fillRect(ox - dw, oy - dh, dw * 2, dh * 2);
      ctx.globalCompositeOperation = "source-over";
      ctx.restore();
    }
  }

  /** Darken/lighten a hex color by multiplier */
  private shadeColor(hex: string, factor: number): string {
    const r = Math.round(parseInt(hex.slice(1, 3), 16) * factor);
    const g = Math.round(parseInt(hex.slice(3, 5), 16) * factor);
    const b = Math.round(parseInt(hex.slice(5, 7), 16) * factor);
    return `rgb(${Math.min(255, r)},${Math.min(255, g)},${Math.min(255, b)})`;
  }

  // ─── Name Field ─────────────────────────────────────────────────────────

  private drawNameField(cx: number, cy: number, w: number, h: number, s: number): void {
    const { ctx } = this;
    const fX = cx - w / 2;
    const fY = cy - h / 2;
    const hovered = this.isHovered(fX, fY, w, h);

    // Label above
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.font = `${Math.round(12 * s)}px Georgia, serif`;
    ctx.fillStyle = hovered || this.nameEditing ? C.text : C.textDim;
    ctx.fillText("Name", cx, fY - 4 * s);
    ctx.restore();

    // Field
    ctx.save();
    if (hovered && !this.nameEditing) {
      ctx.shadowColor = C.warm;
      ctx.shadowBlur = 6;
    }
    ctx.fillStyle = this.nameEditing ? C.nameActiveBg : hovered ? "#1a1410" : C.nameBg;
    this.roundRect(fX, fY, w, h, 5, 5, 5, 5);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = this.nameEditing ? C.accent : hovered ? C.accentDim : C.nameBorder;
    ctx.lineWidth = this.nameEditing ? 2 : hovered ? 1.5 : 1;
    this.roundRect(fX, fY, w, h, 5, 5, 5, 5);
    ctx.stroke();
    ctx.restore();

    // Text
    const fontSize = Math.round(15 * s);
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `${fontSize}px Georgia, serif`;
    if (this.playerName.length === 0 && !this.nameEditing) {
      ctx.fillStyle = C.textDim;
      ctx.fillText("Tap to name\u2026", cx, cy);
    } else {
      ctx.fillStyle = C.nameText;
      let disp = this.playerName;
      if (this.nameEditing && this.cursorVis) disp += "|";
      ctx.fillText(disp, cx, cy, w - 16 * s);
    }
    ctx.restore();

    this.addHit(fX, fY, w, h, () => {
      this.nameEditing = true;
      this.cursorTimer = 0;
      this.cursorVis = true;
      this.playUIClick("name");
    });
  }

  // ─── Begin Button ───────────────────────────────────────────────────────

  private drawBeginButton(cx: number, cy: number, w: number, h: number, s: number): void {
    const { ctx } = this;
    const bX = cx - w / 2;
    const bY = cy - h / 2;
    const hovered = this.isHovered(bX, bY, w, h);
    this.beginBtnHover = hovered;

    // Breathing glow intensity when hovered
    const breathe = hovered ? 1.0 + Math.sin(this.globalTime * 3) * 0.3 : 1.0;

    ctx.save();
    ctx.shadowColor = C.warm;
    ctx.shadowBlur = (hovered ? 30 : 20) * s * breathe;

    // Slight scale-up on hover
    if (hovered) {
      ctx.translate(cx, cy);
      ctx.scale(1.03, 1.03);
      ctx.translate(-cx, -cy);
    }

    const grad = ctx.createLinearGradient(bX, bY, bX, bY + h);
    grad.addColorStop(0, hovered ? C.accentHover : C.accent);
    grad.addColorStop(1, hovered ? C.accent : C.accentDim);
    ctx.fillStyle = grad;
    this.roundRect(bX, bY, w, h, 7, 7, 7, 7);
    ctx.fill();
    ctx.shadowBlur = 0;

    // Specular highlight across the top
    ctx.strokeStyle = "rgba(255,255,255,0.15)";
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(bX + 8, bY + 1);
    ctx.lineTo(bX + w - 8, bY + 1);
    ctx.stroke();

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `bold ${Math.round(16 * s)}px Georgia, serif`;
    ctx.fillStyle = C.btnDark;
    ctx.fillText("Enter Hearth", cx, cy + 1);
    ctx.restore();

    this.addHit(bX, bY, w, h, () => {
      this.playUIClick("begin");
      this.onBegin();
    });
  }

  // ─── Input Processing ──────────────────────────────────────────────────

  private processClick(pos: PointerPosition): void {
    for (let i = this.hits.length - 1; i >= 0; i--) {
      const r = this.hits[i];
      if (pos.x >= r.x && pos.x <= r.x + r.w && pos.y >= r.y && pos.y <= r.y + r.h) {
        r.action();
        return;
      }
    }
    if (this.nameEditing) this.nameEditing = false;
  }

  private processNameKeys(input: InputState): void {
    for (const key of input.keysJustPressed) {
      if (key === "Enter" || key === "Escape") { this.nameEditing = false; return; }
      if (key === "Backspace") { this.playerName = this.playerName.slice(0, -1); return; }
      if (key.length === 1 && this.playerName.length < 20) {
        this.playerName += key;
      }
    }
  }

  // ─── Preview direction ─────────────────────────────────────────────────

  private rotateDirManual(delta: number): void {
    this.manualDir = true;
    this.dirIndex = (this.dirIndex + delta + DIRS.length) % DIRS.length;
    if (this.charState) this.charState.dirty = true;
    if (this.deviceCharState) this.deviceCharState.dirty = true;
    // Resume auto-rotate after 5 seconds
    this.dirTimer = -5;
    setTimeout(() => { this.manualDir = false; }, 5000);
    this.playUIClick("dir");
  }

  // ─── Attribute Logic ───────────────────────────────────────────────────

  private adjustAttr(attr: AttributeDef, delta: number): void {
    // String fields — map index to/from string values
    if (attr.key === "expression") {
      const curIdx = EXPRESSION_VALUES.indexOf(this.appearance.expression);
      let newIdx = (curIdx >= 0 ? curIdx : 0) + delta;
      if (newIdx > attr.max) newIdx = attr.min;
      if (newIdx < attr.min) newIdx = attr.max;
      (this.appearance as unknown as Record<string, string>).expression = EXPRESSION_VALUES[newIdx] ?? "neutral";
    } else if (attr.key === "bodyArchetype1" || attr.key === "bodyArchetype2") {
      const curIdx = ARCHETYPE_IDS.indexOf(this.appearance[attr.key]);
      let newIdx = (curIdx >= 0 ? curIdx : 2) + delta;
      if (newIdx > attr.max) newIdx = attr.min;
      if (newIdx < attr.min) newIdx = attr.max;
      (this.appearance as unknown as Record<string, string>)[attr.key] = ARCHETYPE_IDS[newIdx] ?? "lithe";
    } else if (attr.key === "bodyGender") {
      const curIdx = GENDER_IDS.indexOf(this.appearance.bodyGender);
      let newIdx = (curIdx >= 0 ? curIdx : 2) + delta;
      if (newIdx > attr.max) newIdx = attr.min;
      if (newIdx < attr.min) newIdx = attr.max;
      (this.appearance as unknown as Record<string, string>).bodyGender = GENDER_IDS[newIdx] ?? "androgynous";
    } else if (attr.key === "nude") {
      this.appearance.nude = !this.appearance.nude;
    } else if (attr.key === "archetypeBlend") {
      let intVal = Math.round(this.appearance.archetypeBlend * 4) + delta;
      if (intVal > attr.max) intVal = attr.min;
      if (intVal < attr.min) intVal = attr.max;
      this.appearance.archetypeBlend = intVal / 4;
    } else {
      let val = (this.appearance[attr.key] as number) + delta;
      if (val > attr.max) val = attr.min;
      if (val < attr.min) val = attr.max;
      (this.appearance as unknown as Record<string, number>)[attr.key] = val;
    }

    // Invalidate BOTH character states so all direction caches clear
    if (this.charState) {
      this.charState.appearance = { ...this.appearance };
      invalidateCharacter(this.charState);
    }
    if (this.deviceCharState) {
      this.deviceCharState.appearance = { ...this.appearance, heldItem: this.deviceFocus === "phone" ? 6 : 7 };
      invalidateCharacter(this.deviceCharState);
    }

    // Track for animation pulse
    this.lastChangedAttr = attr.key;
    this.lastChangeTime = this.globalTime;

    // Play UI click sound
    this.playUIClick("adjust");
  }

  private onBegin(): void {
    setComposerAppearance(this.appearance);
    this.buildStarterItems();
    this._ready = true;
  }

  /** Convert outfit choices into inventory items */
  private buildStarterItems(): void {
    this.starterItems = [];
    const now = Date.now();

    const addSlot = (slot: Parameters<typeof getPiecesForSlot>[0], styleIdx: number) => {
      if (styleIdx < 0) return;
      const pieces = getPiecesForSlot(slot).filter(p => p.assetKey !== "");
      const piece = pieces[styleIdx % pieces.length];
      if (piece) {
        this.starterItems.push({
          id: piece.id, type: "clothing", name: piece.name,
          quantity: 1, rarity: piece.rarity, acquiredAt: now,
        });
      }
    };

    addSlot("top",       this.appearance.topStyle);
    addSlot("bottom",    this.appearance.bottomStyle);
    addSlot("shoes",     this.appearance.shoeStyle);
    addSlot("hat",       this.appearance.hatStyle);
    addSlot("jacket",    this.appearance.jacketStyle);
    addSlot("held",      this.appearance.heldItem);
    addSlot("accessory", this.appearance.accessoryStyle);

    // Wings, capes, backpacks don't have wardrobe slots yet — add directly
    const WINGS = ["Angel Wings", "Butterfly Wings", "Bat Wings", "Fairy Wings"];
    const CAPES = ["Short Cape", "Long Cape", "Hooded Cloak"];
    const PACKS = ["Small Pack", "Hiking Pack", "Satchel"];
    if (this.appearance.wingsStyle >= 0) {
      this.starterItems.push({
        id: `wings_${this.appearance.wingsStyle}`, type: "clothing",
        name: WINGS[this.appearance.wingsStyle] ?? "Wings",
        quantity: 1, rarity: "uncommon", acquiredAt: now,
      });
    }
    if (this.appearance.capeStyle >= 0) {
      this.starterItems.push({
        id: `cape_${this.appearance.capeStyle}`, type: "clothing",
        name: CAPES[this.appearance.capeStyle] ?? "Cape",
        quantity: 1, rarity: "uncommon", acquiredAt: now,
      });
    }
    if (this.appearance.backpackStyle >= 0) {
      this.starterItems.push({
        id: `backpack_${this.appearance.backpackStyle}`, type: "clothing",
        name: PACKS[this.appearance.backpackStyle] ?? "Backpack",
        quantity: 1, rarity: "common", acquiredAt: now,
      });
    }
  }

  // ─── Attention Context Helpers ────────────────────────────────────────

  /** Push current attention state to composer module-level globals before rendering */
  private pushAttentionContext(): void {
    const a = this.attentionState;
    setAttentionContext(
      a.gazeX, a.gazeY,
      a.headTiltX, a.headTiltY,
      a.blinkPhase,
      a.hairSwingX, a.hairSettlePhase,
      a.secondaryLagX, a.secondaryLagY,
      a.shoulderTurnX, a.breathIntensity, a.interestLevel,
      a.pupilDilation, a.lidFollowY,
      a.microTremorX, a.microTremorY,
      a.browEngagement,
      a.fidgetArmDY, a.fidgetTorsoX, a.weightShiftPhase,
      a.headYaw, a.headPitch
    );
  }

  /** Reset attention context to zeros so it doesn't bleed into other renders */
  private resetAttentionContext(): void {
    setAttentionContext(0, 0, 0, 0, 0, 0, 0, 0, 0);
  }

  // ─── Drawing Helpers ───────────────────────────────────────────────────

  private addHit(x: number, y: number, w: number, h: number, action: () => void): void {
    this.hits.push({ x, y, w, h, action });
  }

  /** Check if current hover position is inside a rect */
  private isHovered(x: number, y: number, w: number, h: number): boolean {
    return this.hoverX >= x && this.hoverX <= x + w &&
           this.hoverY >= y && this.hoverY <= y + h;
  }

  private drawArrow(cx: number, cy: number, dir: "left" | "right", sz: number): void {
    const { ctx } = this;
    const hovered = this.isHovered(cx - sz * 2, cy - sz * 2, sz * 4, sz * 4);

    ctx.save();

    // Hover glow
    if (hovered) {
      ctx.shadowColor = C.warm;
      ctx.shadowBlur = 8;
    }

    // Breathing scale on hover
    const scale = hovered ? 1.0 + Math.sin(this.globalTime * 6) * 0.08 + 0.12 : 1.0;
    ctx.translate(cx, cy);
    ctx.scale(scale, scale);

    ctx.fillStyle = hovered ? C.accentHover : C.arrow;
    ctx.beginPath();
    if (dir === "left") {
      ctx.moveTo(sz, -sz);
      ctx.lineTo(-sz, 0);
      ctx.lineTo(sz, sz);
    } else {
      ctx.moveTo(-sz, -sz);
      ctx.lineTo(sz, 0);
      ctx.lineTo(-sz, sz);
    }
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  /** Darken a hex color by a factor (0=black, 1=original) */
  private darkenHex(hex: string, factor: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgb(${Math.round(r * factor)},${Math.round(g * factor)},${Math.round(b * factor)})`;
  }

  // ─── UI Audio ────────────────────────────────────────────────────────────

  /** Initialize audio context on first interaction (browser requires user gesture) */
  private ensureAudio(): AudioContext | null {
    if (this.audioCtx) return this.audioCtx;
    try {
      this.audioCtx = new AudioContext();
      this.audioGain = this.audioCtx.createGain();
      this.audioGain.gain.value = 0.12; // subtle — present but not demanding
      this.audioGain.connect(this.audioCtx.destination);
      return this.audioCtx;
    } catch {
      return null;
    }
  }

  /**
   * Play a procedurally synthesized UI click with per-interaction variation.
   * No two clicks sound identical — pitch, timbre, and decay shift slightly.
   *
   * Types:
   *   "adjust"  — attribute arrow tap: crisp, warm, melodic micro-click
   *   "tab"     — category switch: softer, wider, slightly resonant
   *   "begin"   — enter button: rich, affirming tone chord
   *   "dir"     — direction arrow: light, airy tick
   *   "name"    — name field focus: gentle chime
   */
  private playUIClick(type: "adjust" | "tab" | "begin" | "dir" | "name"): void {
    const ctx = this.ensureAudio();
    if (!ctx || !this.audioGain) return;
    if (ctx.state === "suspended") void ctx.resume();

    const now = ctx.currentTime;
    this.clickCounter++;
    // Micro-variation seed: shifts pitch/timing slightly per click
    const v = (this.clickCounter * 7 + 13) % 100 / 100; // 0-1 deterministic pseudo-random

    switch (type) {

      case "adjust": {
        // Crisp click (white noise burst) + warm tone pip
        // Tone shifts up the pentatonic scale based on variation
        const baseFreq = 600 + v * 200; // 600-800 Hz range

        // Noise click
        const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.008), ctx.sampleRate);
        const data = buf.getChannelData(0);
        for (let i = 0; i < data.length; i++) {
          data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
        }
        const click = ctx.createBufferSource();
        click.buffer = buf;
        const clickGain = ctx.createGain();
        clickGain.gain.setValueAtTime(0.5, now);
        clickGain.gain.exponentialRampToValueAtTime(0.001, now + 0.008);
        // BPF to give it warmth
        const bpf = ctx.createBiquadFilter();
        bpf.type = "bandpass"; bpf.frequency.value = 2500 + v * 500; bpf.Q.value = 1.5;
        click.connect(bpf); bpf.connect(clickGain); clickGain.connect(this.audioGain);
        click.start(now); click.stop(now + 0.01);

        // Warm tone pip
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(baseFreq, now);
        osc.frequency.exponentialRampToValueAtTime(baseFreq * 1.02, now + 0.04);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.3, now);
        env.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
        osc.connect(env); env.connect(this.audioGain);
        osc.start(now); osc.stop(now + 0.07);
        break;
      }

      case "tab": {
        // Softer, wider — like a page turning
        const freq = 500 + v * 100;
        const osc = ctx.createOscillator();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(freq, now);
        osc.frequency.exponentialRampToValueAtTime(freq * 0.85, now + 0.1);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.25, now);
        env.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
        osc.connect(env); env.connect(this.audioGain!);
        osc.start(now); osc.stop(now + 0.11);

        // Secondary soft noise layer
        const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.03), ctx.sampleRate);
        const data = buf.getChannelData(0);
        for (let i = 0; i < data.length; i++) {
          data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 2);
        }
        const ns = ctx.createBufferSource();
        ns.buffer = buf;
        const nsGain = ctx.createGain();
        nsGain.gain.setValueAtTime(0.15, now);
        nsGain.gain.exponentialRampToValueAtTime(0.001, now + 0.03);
        const lpf = ctx.createBiquadFilter();
        lpf.type = "lowpass"; lpf.frequency.value = 1200;
        ns.connect(lpf); lpf.connect(nsGain); nsGain.connect(this.audioGain!);
        ns.start(now); ns.stop(now + 0.035);
        break;
      }

      case "begin": {
        // Rich affirming chord — two tones in harmony
        const freqs = [440 + v * 20, 660 + v * 30];
        freqs.forEach((freq, i) => {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.setValueAtTime(freq, now + i * 0.03);
          const env = ctx.createGain();
          env.gain.setValueAtTime(0, now);
          env.gain.linearRampToValueAtTime(0.3, now + i * 0.03 + 0.02);
          env.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
          osc.connect(env); env.connect(this.audioGain!);
          osc.start(now + i * 0.03); osc.stop(now + 0.45);
        });
        break;
      }

      case "dir": {
        // Light airy tick — very short
        const osc = ctx.createOscillator();
        osc.type = "sine";
        const freq = 800 + v * 200;
        osc.frequency.setValueAtTime(freq, now);
        osc.frequency.exponentialRampToValueAtTime(freq * 0.9, now + 0.03);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.2, now);
        env.gain.exponentialRampToValueAtTime(0.001, now + 0.04);
        osc.connect(env); env.connect(this.audioGain!);
        osc.start(now); osc.stop(now + 0.05);
        break;
      }

      case "name": {
        // Gentle chime — slightly longer, inviting
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(880, now);
        osc.frequency.exponentialRampToValueAtTime(1100, now + 0.08);
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.15, now);
        env.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
        osc.connect(env); env.connect(this.audioGain!);
        osc.start(now); osc.stop(now + 0.16);
        break;
      }
    }
  }

  /** Rounded rect with individual corner radii (tl, tr, br, bl) */
  private roundRect(x: number, y: number, w: number, h: number, tl: number, tr: number, br: number, bl: number): void {
    const { ctx } = this;
    ctx.beginPath();
    ctx.moveTo(x + tl, y);
    ctx.lineTo(x + w - tr, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + tr);
    ctx.lineTo(x + w, y + h - br);
    ctx.quadraticCurveTo(x + w, y + h, x + w - br, y + h);
    ctx.lineTo(x + bl, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - bl);
    ctx.lineTo(x, y + tl);
    ctx.quadraticCurveTo(x, y, x + tl, y);
    ctx.closePath();
  }
}
